import type { GlossaryDiagramPattern } from "./glossary-diagram-patterns";

export type GlossaryVisual = {
  pattern: GlossaryDiagramPattern;
  batch?: string;
  src: string;
  mobileSrc: string;
  darkSrc?: string;
  mobileDarkSrc?: string;
  alt: string;
  caption: string;
  width: number;
  height: number;
  mobileWidth: number;
  mobileHeight: number;
};

/**
 * PC の紙の高さは語ごとに 400 から 600 までの 20 刻み。中身の高さ (札の下端から
 * 一言の上まで) に、上下 22px の空きを足して決める。字の大きさと中身の組は変えない。
 * ここに無い語は 600。値は図の viewBox の高さと同じで、check-glossary-public.ts が
 * 突き合わせる。語ごとの値は m6-briefs の paper8.py が出す tiers8.json が正本。
 */
export const GLOSSARY_PC_PAPER_HEIGHTS: readonly number[] = Array.from(
  { length: 11 },
  (_, i) => 600 - i * 20,
);

const PC_HEIGHT_400_SLUGS = [
  "debug", "operations-maintenance", "re-render",
];

const PC_HEIGHT_420_SLUGS = [
  "app-use", "backend", "ci", "component", "input-device", "io",
  "micro-interaction", "semantic-html",
];

const PC_HEIGHT_440_SLUGS = [
  "big-o-notation", "bit", "bubble-sort", "cookie", "css", "delivery", "dom",
  "dynamic-programming", "express-json", "fullstack", "httpsession",
  "internet", "javascript", "nodejs", "overfitting", "prime-number", "spa",
  "ssl-tls", "typescript",
];

const PC_HEIGHT_460_SLUGS = [
  "agent-loop", "async-await", "automated-test", "back-propagation",
  "binary-search", "code-point", "comment", "conflict", "contrast",
  "control-structure", "control-unit", "cross-validation",
  "dependency-direction", "fetch-api", "forward-propagation", "graph",
  "hash-function", "hover", "http", "inline-code", "json", "kebab-case",
  "latency", "layer", "max-width", "merge-2", "operator", "primitive",
  "primitive-type", "prompttemplate", "public-key", "python", "queue",
  "recursion", "responsive-design", "rest-api", "review", "scikit-learn",
  "self-attention", "session-scope", "sidebar", "stack",
  "supervised-learning", "sync", "terminator", "test", "timestamp",
  "tradeoff", "tree-structure", "view", "wireframe",
];

const PC_HEIGHT_480_SLUGS = [
  "activation-bar", "actor", "agent-harness", "agent-runtime", "agent-skills",
  "ai", "alignment", "api", "association", "attribute", "authmiddleware",
  "aws", "border-radius", "box-model", "breakpoint", "broadcast", "browser",
  "bucket", "cash-flow", "cast", "channel", "ci-cd", "cidr", "cloud",
  "cloudfront", "code-block", "command-line", "command-palette", "commit-db",
  "constructor", "container", "convolutional-layer", "cors", "counter",
  "data-literacy", "data-structure", "db-t3-micro", "decision",
  "decisiontree", "dependency-array", "design", "dockerfile", "ecosystem",
  "edge-case", "entry-point", "error-message", "escape",
  "event-preventdefault", "fragment", "hashmap", "hot-reload", "html",
  "hypothesis", "interface-type", "java", "jwt-sign", "jwt-verify", "kernel",
  "library", "list-ds", "loading", "localstorage", "merge-sort", "metadata",
  "method-definition", "middleware", "mobile-first", "module", "multi-stage",
  "multiplicity", "neuron", "object", "object-key", "output-device",
  "overload", "override", "pascal-case", "permission", "pipe", "polymorphism",
  "position", "postgresql", "process-flow", "property", "pseudo-class",
  "redirect", "region", "regression", "rendering", "repository",
  "rnn-transformer", "rollback", "router", "separation-of-concerns",
  "shared-types", "shebang", "shell-script", "snake-case", "stage",
  "status-code", "string-valueof", "system-call", "template", "terminal",
  "termination-condition", "ternary-operator", "thread", "time-complexity",
  "tracing", "type-conversion", "unsupervised-learning", "variable",
  "zero-downtime",
];

const PC_HEIGHT_500_SLUGS = [
  "abstract-class", "algorithm", "architecture", "authentication-filter",
  "better-sqlite3", "camel-case", "cascade", "cd", "character-encoding",
  "class-diagram-2", "classname", "column", "comparison-operator",
  "complexity", "comprehension", "conditional", "conditional-rendering",
  "css-variable", "csv-parse", "custom-hook", "dataclass", "deploy",
  "directory", "divide-and-conquer", "draw-io", "encapsulation", "entity",
  "evaluation", "event-loop", "express-js", "f-string", "field", "form",
  "function-definition", "hash-table", "hashset", "http-method", "httpd",
  "indexerror", "instance", "integer-parseint", "interface-type-2",
  "interpreter", "ioexception", "key-pair", "linear-search", "llamaindex",
  "loop", "media-query", "memory", "mysql", "mysql2-promise", "np-linspace",
  "pay-as-you-go", "plt-bar", "plt-figure", "plt-plot", "read-replica",
  "repetition", "requirements-txt", "response-header",
  "responsibility-segregation", "selection-sort", "servlet-container",
  "setautocommit", "shared-responsibility-model", "sieve-of-eratosthenes",
  "slack", "sns-heatmap", "spread", "stock-deduction", "swimlane",
  "synthetic-event", "transparency", "tree", "type-hint", "url",
];

const PC_HEIGHT_520_SLUGS = [
  "align-items", "alu", "argument", "array", "auto-fill", "bastion-host",
  "box-sizing", "branch", "cdn", "claude-code", "code-review", "content-type",
  "do-while", "docker", "domain", "flex-wrap", "flowchart",
  "functional-dependency", "git", "git-clone", "github", "hosted-zone",
  "immutable", "innerhtml", "jsonify", "junction-table", "justify-content",
  "logical-operator", "merge", "missing-value", "mutable",
  "non-functional-requirement", "one-way-data-flow", "platform-independent",
  "plt-scatter", "pull-request", "query-parameter", "raci", "react-reactnode",
  "rebase", "req-params", "return-value", "scope", "server", "sns-boxplot",
  "software-testing", "standard-library", "storage", "string-manipulation",
  "system-diagram", "team-development", "tsconfig-json", "uselocalstorage",
  "waterfall",
];

const PC_HEIGHT_540_SLUGS = [
  "arrow-function", "bucket-policy", "callback", "class-diagram", "classlist",
  "classlist-toggle", "codex", "coverage", "csp", "curl", "data-type",
  "dependencies", "fetch-api-2", "function", "git-pull", "health-check",
  "image", "immutable-update", "increment", "iterable", "iteration",
  "json-stringify", "package-json", "placeholder", "policy", "pooling-layer",
  "port-80", "presentational-component", "promise", "promise-all", "push",
  "react", "record-db", "reference-type", "repl", "request-body",
  "request-header", "request-scope", "response-ok", "security-group",
  "selector", "semantic-tag", "sequence-diagram", "session-hijacking",
  "session-invalidate", "session-setattribute", "side-effect",
  "ssl-certificate", "staging", "storage-device", "sync-message",
  "time-perf-counter", "try-catch", "try-with-resources", "type-annotation",
  "type-inference", "type-safety", "usefetch", "vectorization", "web-inf",
  "web-xml", "workflow",
];

const PC_HEIGHT_560_SLUGS = [
  "authentication", "authorization", "class", "csrf", "environment-variable",
  "exception-handling", "hashing", "jwt", "oauth", "password", "salt",
  "session", "try-except", "xss",
];

const PC_HEIGHT_SLUG_GROUPS: Array<[number, string[]]> = [
  [400, PC_HEIGHT_400_SLUGS],
  [420, PC_HEIGHT_420_SLUGS],
  [440, PC_HEIGHT_440_SLUGS],
  [460, PC_HEIGHT_460_SLUGS],
  [480, PC_HEIGHT_480_SLUGS],
  [500, PC_HEIGHT_500_SLUGS],
  [520, PC_HEIGHT_520_SLUGS],
  [540, PC_HEIGHT_540_SLUGS],
  [560, PC_HEIGHT_560_SLUGS],
];

const PC_PAPER_HEIGHT_BY_SLUG: Record<string, number> = Object.fromEntries(
  PC_HEIGHT_SLUG_GROUPS.flatMap(([height, slugs]) =>
    slugs.map((slug) => [slug, height]),
  ),
);

function glossaryVisual(
  slug: string,
  pattern: GlossaryDiagramPattern,
  alt: string,
  caption: string,
  batch?: string,
): GlossaryVisual {
  return {
    pattern,
    ...(batch ? { batch } : {}),
    src: `/glossary-diagrams/${slug}.svg`,
    mobileSrc: `/glossary-diagrams/${slug}-mobile.svg`,
    alt,
    caption,
    width: 960,
    height: PC_PAPER_HEIGHT_BY_SLUG[slug] ?? 600,
    mobileWidth: 600,
    mobileHeight: 960,
  };
}

/** 暗い版 (-dark.svg) を持つ新しい描き方の図。 */
function glossaryVisualWithDark(
  slug: string,
  pattern: GlossaryDiagramPattern,
  alt: string,
  caption: string,
): GlossaryVisual {
  return {
    ...glossaryVisual(slug, pattern, alt, caption),
    darkSrc: `/glossary-diagrams/${slug}-dark.svg`,
    mobileDarkSrc: `/glossary-diagrams/${slug}-mobile-dark.svg`,
  };
}

/**
 * 用語集の詳細ページと一覧カードで共有する図解。
 *
 * 図だけを見ても用語の要点が分かるものに限って登録する。本文や alt の
 * 代わりにはせず、JavaScript が無くても表示できる静止 SVG を使う。
 */
export const GLOSSARY_VISUALS: Partial<Record<string, GlossaryVisual>> = {
  aggregation: glossaryVisualWithDark(
    "aggregation",
    "data-flow",
    "集約は、伝票を店ごとの箱に分けて合計する。店の列が同じ伝票を同じ箱へ。元の伝票は残る。元の行は消えない。答えの表だけが小さくなる",
    "元の行は消えない。答えの表だけが小さくなる。",
  ),
  "ai-agent": glossaryVisualWithDark(
    "ai-agent",
    "loop",
    "AI エージェントは、結果を見て次の一手を決め直す。要るだけ何周も回り、終わりの条件で止まる",
    "LLM は道具を自分では動かさない。使う道具を書くと、プログラムが動かして結果を返す。",
  ),
  "connection-pool": glossaryVisualWithDark(
    "connection-pool",
    "architecture",
    "コネクションプールは、貸し傘の傘立て。傘 (接続) を借りて返す。DB が受けられる数にも限りがある。接続の数には上限がある。使い終えたら返す",
    "接続の数には上限がある。使い終えたら返す。",
  ),
  "context-length": glossaryVisualWithDark(
    "context-length",
    "architecture",
    "コンテキスト長は、AI が一度に読める紙の大きさ。ふつうは毎回、必要な過去のやり取りも紙に入れて渡す。ふつうは覚えているのではなく、必要な履歴を毎回渡す",
    "ふつうは覚えているのではなく、必要な履歴を毎回渡す。",
  ),
  deadlock: glossaryVisualWithDark(
    "deadlock",
    "swimlane",
    "デッドロックは、1 車線の橋で向かい合う 2 台。どちらも先へ進めなくなる。DB が見つけて、片方を取り消す。待てば直るものではない",
    "DB が見つけて、片方を取り消す。待てば直るものではない。",
  ),
  embedding: glossaryVisualWithDark(
    "embedding",
    "scatter",
    "Embedding は、意味の近さを数の並びの近さにしたもの。同じ言葉を使っていなくても、近いと分かる。言葉が違っても、意味が近ければ数の並びも近くなりやすい",
    "言葉が違っても、意味が近ければ数の並びも近くなりやすい。",
  ),
  "execution-order": glossaryVisualWithDark(
    "execution-order",
    "process",
    "SQL は、料理の手順で考える。材料を出す、選ぶ、皿に盛る。書く順とは違う。SELECT の別名は、WHERE の時点ではまだ無い",
    "SELECT の別名は、WHERE の時点ではまだ無い。",
  ),
  "few-shot": glossaryVisualWithDark(
    "few-shot",
    "pyramid",
    "Few-shot は、お手本を紙に添えて頼む方法。お手本が 0 個なら Zero-shot。モデル自体は追加学習しない。お手本は、渡した紙の中でだけ効く。モデル自体は変わらない",
    "お手本は、渡した紙の中でだけ効く。モデル自体は変わらない。",
  ),
  "fine-tuning": glossaryVisualWithDark(
    "fine-tuning",
    "state",
    "ファインチューニングは、答え方を整える追加の学習。新しい資料を読ませる用途には、紙に添える RAG が向く。主に変わるのは答え方の癖。最新の資料は紙に添える (RAG)",
    "主に変わるのは答え方の癖。最新の資料は紙に添える (RAG)。",
  ),
  "foreign-key": glossaryVisualWithDark(
    "foreign-key",
    "er",
    "外部キーは、注文票と台帳を結ぶ糸。注文票の顧客番号の欄が、顧客台帳の行を指す。指し先のない値は入らず、指された行はふつう消せない",
    "指し先のない値は入らず、指された行はふつう消せない。",
  ),
  hallucination: glossaryVisualWithDark(
    "hallucination",
    "process",
    "ハルシネーションは、間違いも自信ありげに書く。口調からは、本当か作り話かが分からない。正しさは口調では分からない。出典を開いて確かめる",
    "正しさは口調では分からない。出典を開いて確かめる。",
  ),
  "index-db": glossaryVisualWithDark(
    "index-db",
    "tree",
    "インデックスは、本の巻末の索引。引くのが速くなることがある。そのかわり、書く仕事が増える。付ければ必ず速いわけではない。よく引く列に付ける",
    "付ければ必ず速いわけではない。よく引く列に付ける。",
  ),
  join: glossaryVisualWithDark(
    "join",
    "data-flow",
    "JOIN は、2 冊の台帳に定規を当てる。元の台帳は変わらない。この例は顧客番号で結ぶ。相手のいない行は、ふつうの JOIN では結果に出ない",
    "相手のいない行は、ふつうの JOIN では結果に出ない。",
  ),
  "knowledge-cutoff": glossaryVisualWithDark(
    "knowledge-cutoff",
    "timeline",
    "AI の知識には、学習が止まった日がある。そのあとの出来事は、紙で補わないと知らないことが多い。新しい情報は、AI の中ではなく、紙に添えて渡す",
    "新しい情報は、AI の中ではなく、紙に添えて渡す。",
  ),
  llm: glossaryVisualWithDark(
    "llm",
    "sequence",
    "LLM は、続きの 1 語を選んで足していく。空は見ていない。天気の情報は渡していない。ありそうな続きを選んだだけ。だから、もっともらしい間違いも書く",
    "3 で打った語も紙に足して、また 1 から。終わりの印が出るまで繰り返す。",
  ),
  "machine-learning": glossaryVisualWithDark(
    "machine-learning",
    "scatter",
    "機械学習は、例から規則を見つける。迷惑メールの判定を例に。答え付きの例で学ぶ場合の流れ。規則を書くのは人ではなく、例から決まる",
    "規則を書くのは人ではなく、例から決まる。",
  ),
  "many-to-many": glossaryVisualWithDark(
    "many-to-many",
    "venn",
    "多対多は、間に受講名簿を置く。学生と講座。1 人が何講座も、1 講座に何人も。組み合わせを 1 行ずつ書く。1 マスにカンマで詰めない",
    "組み合わせを 1 行ずつ書く。1 マスにカンマで詰めない。",
  ),
  mcp: glossaryVisualWithDark(
    "mcp",
    "architecture",
    "MCP は、AI と道具をつなぐ共通の差し込み口。AI の名前ではなく、つなぎ方の決まり (線の数は模式図)。道具は 1 回作れば、MCP 対応の AI アプリで使い回せる",
    "道具は 1 回作れば、MCP 対応の AI アプリで使い回せる。",
  ),
  normalization: glossaryVisualWithDark(
    "normalization",
    "medallion",
    "正規化は、住所録を 1 冊にまとめる。顧客の現在の住所を、あちこちに書かず 1 か所にだけ書く。直す場所が 1 つなら食い違わない。読むときは JOIN で合わせる",
    "直す場所が 1 つなら食い違わない。読むときは JOIN で合わせる。",
  ),
  nosql: glossaryVisualWithDark(
    "nosql",
    "nested",
    "NoSQL は、表にこだわらない入れ物。台帳のほかに、書類箱や鍵つきのロッカーもある。表に向く仕事も、箱に向く仕事もある。置き換えではない",
    "表に向く仕事も、箱に向く仕事もある。置き換えではない。",
  ),
  orm: glossaryVisualWithDark(
    "orm",
    "layers",
    "ORM は、コードと表をつなぐ通訳。通訳の裏では SQL が動いている。SQL の知識はいる。書き方で SQL の本数が変わる",
    "SQL の知識はいる。書き方で SQL の本数が変わる。",
  ),
  "prepared-statement": glossaryVisualWithDark(
    "prepared-statement",
    "state",
    "プリペアドは、穴あきの書式に値を書く。文の形は先に決めておき、値は別に書き入れる。値は命令にならない。ただし表名や列名は ? にできない",
    "値は命令にならない。ただし表名や列名は ? にできない。",
  ),
  "primary-key": glossaryVisualWithDark(
    "primary-key",
    "tree",
    "主キーは、会員証の番号のようなもの。名前ではなく、変えずに使う番号で 1 人に決める。空にならず、重ならない。だから 1 行に決まる",
    "空にならず、重ならない。だから 1 行に決まる。",
  ),
  prompt: glossaryVisualWithDark(
    "prompt",
    "high-level-parametric",
    "プロンプトは、AI が読む紙に書く依頼文。今回の相手や用件は、書かないと AI には伝わらない。今回の相手と用件と形は、書くほど答えが具体になる",
    "今回の相手と用件と形は、書くほど答えが具体になる。",
  ),
  rag: glossaryVisualWithDark(
    "rag",
    "data-flow",
    "RAG は、答える直前に資料を添えて渡す。LLM に覚えさせるのではない。探す元の文書を新しくすれば、次の答えから新しい内容を参照できる",
    "探す元の文書を新しくすれば、次の答えから新しい内容を参照できる。",
  ),
  table: glossaryVisualWithDark(
    "table",
    "er",
    "テーブルは、列の決まりを守る台帳。列ごとに、入る値の種類を先に印刷しておく。1 行が 1 件。列の決まりに合わない値は入らない",
    "1 行が 1 件。列の決まりに合わない値は入らない。",
  ),
  token: glossaryVisualWithDark(
    "token",
    "bar",
    "AI は文を、トークンという小片に切って読む。語や文字の区切りと一致するとは限らない。切り方はモデルごとに違う。料金も上限も、字数ではなくトークンの数が基準になる",
    "料金も上限も、字数ではなくトークンの数が基準になる。",
  ),
  "tool-calling": glossaryVisualWithDark(
    "tool-calling",
    "sequence",
    "ツール呼び出しは、LLM が頼み、アプリが動かす。LLM は道具を自分では動かさない。頼むのは LLM、動かすのはアプリ。結果をまた LLM が読む",
    "頼むのは LLM、動かすのはアプリ。結果をまた LLM が読む。",
  ),
  transaction: glossaryVisualWithDark(
    "transaction",
    "sequence",
    "トランザクションは、封をした封筒。A から B へ 1,000 円を送る振込の例。途中で失敗したら、ROLLBACK で始める前の状態に戻る",
    "途中で失敗したら、ROLLBACK で始める前の状態に戻る。",
  ),
  "vector-database": glossaryVisualWithDark(
    "vector-database",
    "scatter",
    "ベクトルデータベースは、近い順に取り出す棚。言葉の一致を探すのではない。数の近さで探す。返すのは数ではなく、近い順の文章",
    "返すのは数ではなく、近い順の文章。",
  ),
  "abstract-class": glossaryVisualWithDark(
    "abstract-class",
    "architecture",
    "抽象クラスは、空欄つきの設計図で、そのままでは作れない。子クラスが空欄を埋めて、はじめて物を作れる。空欄は子が埋める。埋めたクラスだけが、物を作れる",
    "空欄は子が埋める。埋めたクラスだけが、物を作れる。",
  ),
  "access-modifier": glossaryVisualWithDark(
    "access-modifier",
    "flowchart",
    "アクセス修飾子は、触ってよい口を決める。全部 public だと、外から書き換えられる。触らせたい口だけ public、中身は private",
    "触らせたい口だけ public、中身は private。",
  ),
  "acid": glossaryVisualWithDark(
    "acid",
    "org-chart",
    "ACID は、4 つの約束の頭文字。銀行の振込を例に、通帳、仕切り板、金庫で 1 つずつ見る。全部か無か、決まりを守る、途中が見えない、確定したら残る",
    "全部か無か、決まりを守る、途中が見えない、確定したら残る。",
  ),
  "activation-bar": glossaryVisualWithDark(
    "activation-bar",
    "sequence",
    "活性区間は、処理を実行している間の帯。処理を実行している間に描く。この例では呼ばれてから戻るまで。点線は存在している間、帯は処理を実行している間",
    "点線は存在している間、帯は処理を実行している間。",
  ),
  "actor": glossaryVisualWithDark(
    "actor",
    "nested",
    "アクターは、システムの外でやり取りする役割。人でなくても、境界の外からやり取りすればアクターになる。棒人間かどうかではなく、境界の外でやり取りするかどうかで決める",
    "棒人間かどうかではなく、境界の外でやり取りするかどうかで決める。",
  ),
  "agent-harness": glossaryVisualWithDark(
    "agent-harness",
    "high-level",
    "Harness は、モデルにつける馬具。賢さそのものではなく、モデルの外で働かせるしくみ。同じモデルでも、馬具しだいで引ける荷が変わる",
    "同じモデルでも、馬具しだいで引ける荷が変わる。",
  ),
  "agent-loop": glossaryVisualWithDark(
    "agent-loop",
    "loop",
    "Loop は、終わるまで周回する線路。1 回聞いて 1 回答えるのではなく、道具を使い、結果を見て、また考える。止める条件に当たるまで、考えて使って見るを繰り返す",
    "止める条件に当たるまで、考えて使って見るを繰り返す。",
  ),
  "agent-runtime": glossaryVisualWithDark(
    "agent-runtime",
    "architecture",
    "Runtime は、エージェントが動く作業場。モデルのサーバーではなく、指示を実行に変える場所。指示を実行に変え、時間と権限と状態を受け持つ",
    "指示を実行に変え、時間と権限と状態を受け持つ。",
  ),
  "agent-skills": glossaryVisualWithDark(
    "agent-skills",
    "flowchart",
    "Skills は、必要なときだけ開くレシピカード。全部を毎回の指示に詰めず、名前と説明だけ先に見せておく。手順と道具を 1 枚にまとめ、使う場面で初めて読み込む",
    "手順と道具を 1 枚にまとめ、使う場面で初めて読み込む。",
  ),
  "aggregate-function": glossaryVisualWithDark(
    "aggregate-function",
    "data-flow",
    "集約関数は、数取器で数えるようなもの。COUNT(*) と COUNT(列) は、空欄 (NULL) の扱いが違う。NULL の行も数えるのは COUNT(*) だけ。COUNT(列)、SUM、AVG は飛ばす",
    "NULL の行も数えるのは COUNT(*) だけ。COUNT(列)、SUM、AVG は飛ばす。",
  ),
  "agile": glossaryVisualWithDark(
    "agile",
    "gantt",
    "アジャイルは、計画をやめることではない。短い周期で小さく作って見せ、客の返事で付箋を入れ替える。計画は、短い周期ごとに立て直す",
    "計画は、短い周期ごとに立て直す。",
  ),
  "ai": glossaryVisualWithDark(
    "ai",
    "state",
    "AI は、入れ子になった人形の総称。ChatGPT という 1 つの製品ではなく、知的な働きをさせる技術の全体。機械学習も今の生成AI も、AI という大きな人形の内側にいる",
    "機械学習も今の生成AI も、AI という大きな人形の内側にいる。",
  ),
  "algorithm": glossaryVisualWithDark(
    "algorithm",
    "bar",
    "アルゴリズムは、答えまでの手順書。レシピのように、順番に書いた手順。同じ答えでも、手順しだいで手間は大きく変わる",
    "同じ答えでも、手順しだいで手間は大きく変わる。",
  ),
  "align-items": glossaryVisualWithDark(
    "align-items",
    "er",
    "align-items は、並ぶ向きと直角に物をそろえる。Flexbox で横並びのとき、そろえるのは上下の位置。横並びなら、上と下の話。左右の余りの配り方は justify-content",
    "横並びなら、上と下の話。左右の余りの配り方は justify-content。",
  ),
  "alignment": glossaryVisualWithDark(
    "alignment",
    "timeline",
    "整列は、決めた順に並べ替えること。見た目をそろえる話ではなく、順番の話。並べ替えておくと、探すときに半分ずつ捨てられる",
    "並べ替えておくと、探すときに半分ずつ捨てられる。",
  ),
  "alu": glossaryVisualWithDark(
    "alu",
    "swimlane",
    "演算装置は、指示された計算だけをする部品。命令を読んで決めるのは、制御装置。命令を読むのは制御装置。演算装置 (ALU) は、指示された計算をして返す",
    "命令を読むのは制御装置。演算装置 (ALU) は、指示された計算をして返す。",
  ),
  "api": glossaryVisualWithDark(
    "api",
    "dp-integration",
    "API は、自動販売機のボタンの一覧。プログラムから使う窓口。中の仕組みは知らなくていい。使えるのは、用意されたボタンだけ",
    "使えるのは、用意されたボタンだけ。",
  ),
  "app-use": glossaryVisualWithDark(
    "app-use",
    "process",
    "app.use は、登録した順に並ぶ関所。合う関所を登録した順に通り、順番が効く。next() を呼ぶと次の関所へ進む。応答も next() もなければ待ち続ける",
    "next() を呼ぶと次の関所へ進む。応答も next() もなければ待ち続ける。",
  ),
  "architecture": glossaryVisualWithDark(
    "architecture",
    "quadrant",
    "アーキテクチャは、部屋の分け方とドアの決め方。技術の名前を並べる話ではなく、どう分けてどうつなぐか。技術を並べても決まらない。分け方とつなぎ方が、設計の土台になる",
    "技術を並べても決まらない。分け方とつなぎ方が、設計の土台になる。",
  ),
  "argument": glossaryVisualWithDark(
    "argument",
    "nested",
    "引数は、原本のコピーを渡すようなもの。数や文字を渡しても、呼んだ側の変数は変わらない。関数の中で入れ直しても、元の変数は 5 のまま",
    "関数の中で入れ直しても、元の変数は 5 のまま。",
  ),
  "array": glossaryVisualWithDark(
    "array",
    "bar",
    "配列は、0 番から並ぶ部品箱。3 つ入れたら、番号は 0、1、2。番号は 0 から数える。3 つ入りなら、3 番は無い",
    "番号は 0 から数える。3 つ入りなら、3 番は無い。",
  ),
  "arrow-function": glossaryVisualWithDark(
    "arrow-function",
    "tree",
    "アロー関数は、左を入れて右を返す。x => x * 2 は、x を入れると 2 倍が出る機械。{ } を付けたら、返す値に return が要る",
    "{ } を付けたら、返す値に return が要る。",
  ),
  "association": glossaryVisualWithDark(
    "association",
    "org-chart",
    "関連は、クラスどうしをつなぐ 1 本の線。線の両端の数字で、何対何かも書く。関連は、つながりを示す線。継承 (白い三角) とは別のもの",
    "関連は、つながりを示す線。継承 (白い三角) とは別のもの。",
  ),
  "async": glossaryVisualWithDark(
    "async",
    "layers",
    "非同期は、待たずに次へ進み、あとで受け取る。待ち時間に、ほかの仕事を進められる。同時に動くとは限らない。待ち時間をほかの仕事に使う",
    "同時に動くとは限らない。待ち時間をほかの仕事に使う。",
  ),
  "async-await": glossaryVisualWithDark(
    "async-await",
    "venn",
    "async/await は、呼び出しブザーを持って待つ。並行して動く作業員ではなく、待つ間に席を譲るしくみ。待つ間はほかの処理に席を譲り、終わったら続きから動く",
    "待つ間はほかの処理に席を譲り、終わったら続きから動く。",
  ),
  "attribute": glossaryVisualWithDark(
    "attribute",
    "pyramid",
    "属性は、オブジェクトにぶら下がった名前付きの値。m.name のように、ドットでたどって取り出す。m.name は値の Aki。その値に () を付けて呼ぶと失敗する",
    "m.name は値の Aki。その値に () を付けて呼ぶと失敗する。",
  ),
  "authentication": glossaryVisualWithDark(
    "authentication",
    "flowchart",
    "認証は、名乗ったあとに証拠を見せること。ID は名乗りで、誰でも言える。確かめるのは証拠のほう。種類の違う証拠を 2 つ重ねると強くなる",
    "種類の違う証拠を 2 つ重ねると強くなる。",
  ),
  "authentication-filter": glossaryVisualWithDark(
    "authentication-filter",
    "radar",
    "認証フィルターは、処理の手前でログインを確かめる門番。保護された依頼は、毎回この門を通る (Java の Servlet の例)。ログイン画面だけでなく、依頼のたびに確かめる",
    "ログイン画面だけでなく、依頼のたびに確かめる。",
  ),
  "authmiddleware": glossaryVisualWithDark(
    "authmiddleware",
    "loop",
    "authMiddleware は、ルートの前の関所。ログイン処理ではなく、通すか止めるかの検問。関所はルートの前に書く。通れば next、だめなら 401",
    "関所はルートの前に書く。通れば next、だめなら 401。",
  ),
  "authorization": glossaryVisualWithDark(
    "authorization",
    "data-lake",
    "認可は、ドアごとに毎回確かめること。ログインできても、開くドアは決まっている。ボタンを隠すだけでは守れない。サーバーが毎回決める",
    "ボタンを隠すだけでは守れない。サーバーが毎回決める。",
  ),
  "auto-fill": glossaryVisualWithDark(
    "auto-fill",
    "bar",
    "auto-fill は、幅に入るだけ列を作る。repeat(auto-fill, minmax(100px, 1fr)) の列の数 (gap なし)。物が少なくても、空の列は残る",
    "物が少なくても、空の列は残る。",
  ),
  "autocommit": glossaryVisualWithDark(
    "autocommit",
    "line",
    "自動コミットは、1 文ごとに判子を押す機械。2 文を 1 組にしたいなら、BEGIN で始めて自分で押す。既定では 1 文ごとに確定される。1 組にするなら BEGIN で始め、COMMIT で終える",
    "既定では 1 文ごとに確定される。1 組にするなら BEGIN で始め、COMMIT で終える。",
  ),
  "automated-test": glossaryVisualWithDark(
    "automated-test",
    "gantt",
    "自動テストは、毎回動く検品の機械。同じ確認を、機械が速く繰り返す。書いたテストの範囲を、機械が何度でも確かめる。壊れた所に気づきやすい",
    "書いたテストの範囲を、機械が何度でも確かめる。壊れた所に気づきやすい。",
  ),
  "aws": glossaryVisualWithDark(
    "aws",
    "architecture",
    "AWS は、サービスを選ぶ売り場。1 つの製品ではなく、役割ごとの部品が棚に並んでいる。必要な棚から部品を取って組み合わせる。全部は使わない",
    "必要な棚から部品を取って組み合わせる。全部は使わない。",
  ),
  "back-propagation": glossaryVisualWithDark(
    "back-propagation",
    "loop",
    "逆伝播は、各つまみの責任を後ろへ配る。つまみを直すのは、そのあとの別の一手。loss.backward() で責任 (勾配) を求め、optimizer.step() で直す",
    "loss.backward() で責任 (勾配) を求め、optimizer.step() で直す。",
  ),
  "backend": glossaryVisualWithDark(
    "backend",
    "architecture",
    "バックエンドは、画面を信じないレジ。画面の値も、送る値も、手元で書き換えられる。価格・在庫・権限の最後の判断は、台帳を持つサーバーでする",
    "価格・在庫・権限の最後の判断は、台帳を持つサーバーでする。",
  ),
  "bastion-host": glossaryVisualWithDark(
    "bastion-host",
    "scatter",
    "踏み台は、内側のサーバーへ入る入口を絞った中継の 1 台。外から直接は入れず、踏み台を経由して入る。入口を絞ると、見張る場所を減らせる。奥の認証は別に要る",
    "入口を絞ると、見張る場所を減らせる。奥の認証は別に要る。",
  ),
  "better-sqlite3": glossaryVisualWithDark(
    "better-sqlite3",
    "medallion",
    "better-sqlite3 は、机の上の帳簿をその場で開く道具。Node.js から SQLite を使うライブラリ。await は要らない。別のサーバーは不要。SQL を同期で実行して、結果を受け取る",
    "別のサーバーは不要。SQL を同期で実行して、結果を受け取る。",
  ),
  "big-o-notation": glossaryVisualWithDark(
    "big-o-notation",
    "high-level",
    "ビッグ O は、手間の増え方の目印。秒数ではなく、データが増えたときの形。データが増えたときの、手間の増え方で比べる",
    "データが増えたときの、手間の増え方で比べる。",
  ),
  "binary-search": glossaryVisualWithDark(
    "binary-search",
    "process",
    "二分探索は、辞書を真ん中から開く探し方。並んでいるから、半分を捨てられる。番号順に並んでいれば、1 回で探す範囲が半分になる",
    "番号順に並んでいれば、1 回で探す範囲が半分になる。",
  ),
  "binary-tree": glossaryVisualWithDark(
    "binary-tree",
    "data-flow",
    "二分木は、枝が 2 本までの形。大小の決まりは入っていない。入れると二分探索木になる。決まりを足すと、二分探索木になる",
    "決まりを足すと、二分探索木になる。",
  ),
  "bit": glossaryVisualWithDark(
    "bit",
    "dp-integration",
    "ビットは、スイッチ 1 個のオンとオフ。オフが 0、オンが 1 で、2 通りだけを持つ。スイッチが 1 個増えるたび、表せる通りが 2 倍になる",
    "スイッチが 1 個増えるたび、表せる通りが 2 倍になる。",
  ),
  "border-radius": glossaryVisualWithDark(
    "border-radius",
    "high-level-parametric",
    "角丸は、四角の角を丸める半径の値。border-radius は、円や楕円の弧で角を丸める。値は角に当てる円や楕円の半径。50% は幅と高さの半分で、長方形だと楕円になる",
    "値は角に当てる円や楕円の半径。50% は幅と高さの半分で、長方形だと楕円になる。",
  ),
  "box-model": glossaryVisualWithDark(
    "box-model",
    "dp-security-matrix",
    "ボックスモデルは、中身に余白と枠を重ねた箱。CSS は、どの要素も 4 層の四角として扱う。width は中身の幅だけ。余白と枠は外に足される",
    "width は中身の幅だけ。余白と枠は外に足される。",
  ),
  "box-sizing": glossaryVisualWithDark(
    "box-sizing",
    "it-current-state",
    "box-sizing は、width が指す範囲を決める。width 200px、内側の余白 20px、枠 5px のとき、見える幅はいくつか。既定の content-box では、width 200px でも見える幅は 250px",
    "既定の content-box では、width 200px でも見える幅は 250px。",
  ),
  "branch": glossaryVisualWithDark(
    "branch",
    "tree",
    "ブランチは、貨車に付ける名札。名札を作っても、貨車 (コミット) は増えない。名札を 1 枚付けるだけ。ファイルの複製ではない",
    "名札を 1 枚付けるだけ。ファイルの複製ではない。",
  ),
  "breakpoint": glossaryVisualWithDark(
    "breakpoint",
    "sequence-oauth",
    "ブレークポイントは、行の手前で止める遮断機。止まった行は、まだ実行されていない。止まった所で変数を見て、1 行ずつ進める",
    "止まった所で変数を見て、1 行ずつ進める。",
  ),
  "broadcast": glossaryVisualWithDark(
    "broadcast",
    "architecture",
    "ブロードキャストは、同じ部屋の全員への一斉の呼びかけ。ルーターの壁の先までは届かない。同じネットワークの全員に 1 回で届く。ルーターを越えると届かない",
    "同じネットワークの全員に 1 回で届く。ルーターを越えると届かない。",
  ),
  "browser": glossaryVisualWithDark(
    "browser",
    "nested",
    "ブラウザは、届いた文字を絵に直す窓。最初に届くのは HTML の文字。絵にするのは手元。HTML を読んで描き、JavaScript も動かす実行の場所",
    "HTML を読んで描き、JavaScript も動かす実行の場所。",
  ),
  "bubble-sort": glossaryVisualWithDark(
    "bubble-sort",
    "flowchart",
    "バブルソートは、隣どうしの背比べ。隣を比べて入れ替え、端まで進む。1 周ごとに、いちばん大きい物が右端に浮かぶ",
    "1 周ごとに、いちばん大きい物が右端に浮かぶ。",
  ),
  "bucket": glossaryVisualWithDark(
    "bucket",
    "data-lake",
    "バケットは、荷札で探す平らな倉庫。S3 の汎用バケットに階層はなく、名前 (キー) で 1 つずつ取り出す。images/2026/logo.png は階層ではなく、ひと続きの名前",
    "images/2026/logo.png は階層ではなく、ひと続きの名前。",
  ),
  "bucket-policy": glossaryVisualWithDark(
    "bucket-policy",
    "sequence",
    "バケットポリシーは、バケットに貼る許可証。誰が、何を、どれにしてよいかを書く。パブリックアクセスをすべてブロックなら公開は効かない。許可証を貼っても、すべてブロックの鉄扉が閉じていれば公開されない",
    "許可証を貼っても、すべてブロックの鉄扉が閉じていれば公開されない。",
  ),
  "cache": glossaryVisualWithDark(
    "cache",
    "state",
    "キャッシュは、近くに置いた控え。冷蔵庫と同じ。速いが、古くなることがある。元が変わっても、控えは自動では新しくならない",
    "元が変わっても、控えは自動では新しくならない。",
  ),
  "callback": glossaryVisualWithDark(
    "callback",
    "er",
    "コールバックは、渡しておいて相手に呼んでもらう関数。例はタイマー。すぐ呼ぶ場合もある。渡すのは done そのもの。done() と書くと、その場で呼んでしまう",
    "渡すのは done そのもの。done() と書くと、その場で呼んでしまう。",
  ),
  "callback-function": glossaryVisualWithDark(
    "callback-function",
    "timeline",
    "コールバックは、「呼ぶ先」を渡すメモ。関数名だけを渡す。() を付けると、その場で動く。f は「呼ぶ先」のメモ。f() は「今すぐ呼ぶ」",
    "f は「呼ぶ先」のメモ。f() は「今すぐ呼ぶ」",
  ),
  "camel-case": glossaryVisualWithDark(
    "camel-case",
    "swimlane",
    "キャメルケースは、2 語目から頭を大文字にしてつなぐ。getUserName のように、区切りの記号を使わない書き方。最初の単語は小文字のまま。全部の頭が大文字になるのは別の名前",
    "最初の単語は小文字のまま。全部の頭が大文字になるのは別の名前。",
  ),
  "cascade": glossaryVisualWithDark(
    "cascade",
    "quadrant",
    "カスケードは、規則の重さを量る天秤。後ろに書いた規則が、いつも勝つわけではない。重さの大きい規則が勝つ。同じ重さなら、後ろに書いた方が勝つ",
    "重さの大きい規則が勝つ。同じ重さなら、後ろに書いた方が勝つ。",
  ),
  "cash-flow": glossaryVisualWithDark(
    "cash-flow",
    "nested",
    "キャッシュフローは、財布の中身の増減。帳簿の利益が出ていても、現金は減ることがある。利益は帳簿の数字、キャッシュフローは現金の出入り。別のものとして見る",
    "利益は帳簿の数字、キャッシュフローは現金の出入り。別のものとして見る。",
  ),
  "cast": glossaryVisualWithDark(
    "cast",
    "tree",
    "キャストは、別の型の升に入れ直すこと。小数を整数にすると、小数の部分は切り捨てになる。(int) 3.7 は 3 になる。元の x は 3.7 のまま",
    "(int) 3.7 は 3 になる。元の x は 3.7 のまま。",
  ),
  "cd": glossaryVisualWithDark(
    "cd",
    "org-chart",
    "cd は、今いる場所の札を動かす。フォルダの中身は 1 つも動かない。cd で変わるのは、今いる場所だけ",
    "cd で変わるのは、今いる場所だけ。",
  ),
  "cdn": glossaryVisualWithDark(
    "cdn",
    "sequence",
    "CDN は、近くの店に置く在庫。遠い本店まで毎回行かず、近くの店のコピーを渡す。在庫があれば近くの店から。なければ本店から取り寄せる",
    "在庫があれば近くの店から。なければ本店から取り寄せる。",
  ),
  "chain-of-thought": glossaryVisualWithDark(
    "chain-of-thought",
    "process",
    "途中式を書かせると、答えが当たりやすい。計算用紙に書いた行が、次の行の材料になる。頭の中で考えて、答えだけ出すのではない。書いた字が材料",
    "頭の中で考えて、答えだけ出すのではない。書いた字が材料。",
  ),
  "channel": glossaryVisualWithDark(
    "channel",
    "layers",
    "チャンネルは、話題ごとに分かれた部屋。書き込みは、話題に合う部屋へ。チャンネルは、話題ごとの部屋。書く内容に合う部屋を選ぶ",
    "チャンネルは、話題ごとの部屋。書く内容に合う部屋を選ぶ。",
  ),
  "character-encoding": glossaryVisualWithDark(
    "character-encoding",
    "venn",
    "文字コードは、文字を番号にして保存する対応表。番号はバイトの並びで、読むときも同じ表が要る。書いた表と読む表がそろって、元の文字に戻る",
    "書いた表と読む表がそろって、元の文字に戻る。",
  ),
  "chatgpt": glossaryVisualWithDark(
    "chatgpt",
    "architecture",
    "ChatGPT は、LLM につながる窓口。話しかける画面と、奥で文を作る機械は別の物。同じ機械に、別のアプリの窓口もつながる",
    "同じ機械に、別のアプリの窓口もつながる。",
  ),
  "chunk": glossaryVisualWithDark(
    "chunk",
    "pyramid",
    "チャンクは、長い文書を切り分けた塊。探すときは、塊の単位で取り出す。大きいと別の話が混ざり、小さいと意味が切れる。切り目を重ねる手もある",
    "大きいと別の話が混ざり、小さいと意味が切れる。切り目を重ねる手もある。",
  ),
  "ci": glossaryVisualWithDark(
    "ci",
    "radar",
    "CI は、push をきっかけに回る検査ゲート。変更をこまめに統合し、ビルドとテストを自動で回す。CI は検査まで。公開や配置 (デプロイ) は、また別の工程",
    "CI は検査まで。公開や配置 (デプロイ) は、また別の工程。",
  ),
  "ci-cd": glossaryVisualWithDark(
    "ci-cd",
    "loop",
    "CI/CD は、検品と出荷のライン。CI は毎回の検品係、CD は合格した箱を届ける運び屋。CI で毎回確かめ、CD で届ける。2 つは別の仕事",
    "CI で毎回確かめ、CD で届ける。2 つは別の仕事。",
  ),
  "cidr": glossaryVisualWithDark(
    "cidr",
    "loop",
    "CIDR は、住所の頭を固定する書き方。/ のあとの数は、先頭から固定するビットの数。数が大きいほど固定が多く、使える範囲は狭い",
    "数が大きいほど固定が多く、使える範囲は狭い。",
  ),
  "class": glossaryVisualWithDark(
    "class",
    "data-lake",
    "クラスは、クッキーの型抜き。型を書いただけでは、クッキーは 1 枚もできない。型から抜いた 1 枚ずつが実物 (インスタンス)。味は別々",
    "型から抜いた 1 枚ずつが実物 (インスタンス)。味は別々。",
  ),
  "class-diagram": glossaryVisualWithDark(
    "class-diagram",
    "bar",
    "クラス構成図は、クラスどうしの関係を線で読む図。見るのは箱の中身ではなく、箱と箱をつなぐ線。線の形が関係を決める。実線の先に中抜きの三角なら、継承 (一種)",
    "線の形が関係を決める。実線の先に中抜きの三角なら、継承 (一種)",
  ),
  "class-diagram-2": glossaryVisualWithDark(
    "class-diagram-2",
    "line",
    "クラス図の箱は、3 段のカードで書く。基本は、上から、名前、持つ値、できること。頭の + は外から使える、- は中だけ。コードの public と private に当たる",
    "頭の + は外から使える、- は中だけ。コードの public と private に当たる。",
  ),
  "classlist": glossaryVisualWithDark(
    "classlist",
    "flowchart",
    "classList は、指定した class だけを足し引きする道具。className に代入すると、残したい class も消える。className は全部入れ替え、classList は指定分だけ動かす",
    "className は全部入れ替え、classList は指定分だけ動かす。",
  ),
  "classlist-toggle": glossaryVisualWithDark(
    "classlist-toggle",
    "state",
    "classList.toggle は、あれば外し、なければ付ける。押すたびに class が増えるのではなく、付く、外れるを交互にくり返す。toggle は、いまの有無を反転して、付いたかどうかを返す",
    "toggle は、いまの有無を反転して、付いたかどうかを返す。",
  ),
  "classname": glossaryVisualWithDark(
    "classname",
    "gantt",
    "className は、JSX での class の書き方。画面に出る HTML では class になる。JSX では className、HTML では class になる",
    "JSX では className、HTML では class になる。",
  ),
  "claude-code": glossaryVisualWithDark(
    "claude-code",
    "scatter",
    "Claude Code は、端末の中で作業まで進める AI。Anthropic のコーディング AI。確認を取りながら自分で進める。頼む、許可する、結果を見る。作業は端末の中で進む",
    "頼む、許可する、結果を見る。作業は端末の中で進む。",
  ),
  "clickjacking": glossaryVisualWithDark(
    "clickjacking",
    "medallion",
    "クリックジャッキングは、透明な枠の重ね置き。押したつもりの場所で、別のボタンを押させる。他サイトの枠に入れない、とサイトの側が宣言する",
    "他サイトの枠に入れない、とサイトの側が宣言する。",
  ),
  "cloud": glossaryVisualWithDark(
    "cloud",
    "layers",
    "クラウドは、機械を借りる仕組み。雲の中ではなく、どこかの建物の機械を使っている。自分で買わず、事業者の機械を使った分だけ借りる",
    "自分で買わず、事業者の機械を使った分だけ借りる。",
  ),
  "cloudfront": glossaryVisualWithDark(
    "cloudfront",
    "high-level",
    "CloudFront は、近くの配達拠点。保管場所ではなく、元の置き場の前に置く中継。期限の間は拠点の写しが出る。早く直したいときは無効化する",
    "期限の間は拠点の写しが出る。早く直したいときは無効化する。",
  ),
  "code-block": glossaryVisualWithDark(
    "code-block",
    "nested",
    "コードブロックは、{ } で囲んだひとまとまり。中で const で作った変数は、中だけで見える。外から読むと ReferenceError。外でも使うなら外で作る",
    "外から読むと ReferenceError。外でも使うなら外で作る。",
  ),
  "code-point": glossaryVisualWithDark(
    "code-point",
    "process",
    "コードポイントは、字ごとに振られた番号。保存するバイト数とは、別の話。コードポイントは、字の番号。保存のバイト数は別に決まる",
    "コードポイントは、字の番号。保存のバイト数は別に決まる。",
  ),
  "code-review": glossaryVisualWithDark(
    "code-review",
    "swimlane",
    "コードレビューは、マージ前に別の目で読む原稿チェック。失敗探しではなく、バグや読みにくさを減らす 2 人目の目。コメントを直し、Approve など条件がそろったらマージへ",
    "コメントを直し、Approve など条件がそろったらマージへ。",
  ),
  "codex": glossaryVisualWithDark(
    "codex",
    "data-flow",
    "Codex は、囲いの中で作業するコーディングエージェント。OpenAI の AI。作業フォルダの中は書けるが、外は許可が要る。囲いの中は自由に、外へ出るなら許可を取る",
    "囲いの中は自由に、外へ出るなら許可を取る。",
  ),
  "column": glossaryVisualWithDark(
    "column",
    "dp-integration",
    "カラムは、台帳を縦に区切った 1 つの項目。項目ごとに名前があり、値が縦に並ぶ。SELECT で列を選ぶと、結果にはその縦の項目だけが並ぶ",
    "SELECT で列を選ぶと、結果にはその縦の項目だけが並ぶ。",
  ),
  "command-line": glossaryVisualWithDark(
    "command-line",
    "high-level-parametric",
    "コマンドラインは、1 行の注文文。呪文の暗記ではなく、部品を組み合わせた文。コマンド、オプション、対象の順に並べれば、頼みが通る",
    "コマンド、オプション、対象の順に並べれば、頼みが通る。",
  ),
  "command-palette": glossaryVisualWithDark(
    "command-palette",
    "flowchart",
    "コマンドパレットは、機能を名前で呼び出す窓。メニューのどこにあるか、覚えなくてよい。VS Code では Cmd+Shift+P (Windows は Ctrl+Shift+P) で開き、名前の一部を打つ",
    "VS Code では Cmd+Shift+P (Windows は Ctrl+Shift+P) で開き、名前の一部を打つ。",
  ),
  "comment": glossaryVisualWithDark(
    "comment",
    "dp-security-matrix",
    "コメントは、人のために書く実行されないメモ。コードの横に貼る付箋で、プログラムは読み飛ばす。コメントは、実行されない。行頭に // を付けると、その行は動かない",
    "コメントは、実行されない。行頭に // を付けると、その行は動かない。",
  ),
  "commit": glossaryVisualWithDark(
    "commit",
    "state",
    "COMMIT は、下書きに確定の判子を押すこと。BEGIN のあとの UPDATE は鉛筆の下書き。判子で正式な結果になる。押す前なら消せる。押したあとは戻せず、確定後の新しい取引に新しい値が見える",
    "押す前なら消せる。押したあとは戻せず、確定後の新しい取引に新しい値が見える。",
  ),
  "commit-db": glossaryVisualWithDark(
    "commit-db",
    "it-current-state",
    "コミットは、下書きの変更を確定する印。Git の commit とは別の、DB の操作。コミットは、確定の印。押すまで変更はほかの接続に見えない",
    "コミットは、確定の印。押すまで変更はほかの接続に見えない。",
  ),
  "comparison-operator": glossaryVisualWithDark(
    "comparison-operator",
    "sequence-oauth",
    "比較演算子は、左右を比べて True か False を返す。= は入れる、== は比べる。答えは 2 択の片方。= は入れる、== は比べる。比べた答えは True か False",
    "= は入れる、== は比べる。比べた答えは True か False。",
  ),
  "compile": glossaryVisualWithDark(
    "compile",
    "architecture",
    "コンパイルは、実行の前に全文を訳して製本する。C の場合。原書 hello.c を、翻訳本 hello にする。訳せたら、翻訳本は何度でも実行できる。直したら、訳し直す",
    "訳せたら、翻訳本は何度でも実行できる。直したら、訳し直す。",
  ),
  "complexity": glossaryVisualWithDark(
    "complexity",
    "flowchart",
    "計算量は、増えたときの手数と場所。かかった秒数そのものではない。データが増えたとき、手数と場所がどれだけ増えるかの物差し",
    "データが増えたとき、手数と場所がどれだけ増えるかの物差し。",
  ),
  "component": glossaryVisualWithDark(
    "component",
    "tree",
    "コンポーネントは、画面の型抜き。コピペでなく、同じ型から何枚も作る。ひな形を 1 つ作り、中身を渡して使い回す。直せば、使った所すべてに反映される",
    "ひな形を 1 つ作り、中身を渡して使い回す。直せば、使った所すべてに反映される。",
  ),
  "composite-index": glossaryVisualWithDark(
    "composite-index",
    "sequence",
    "複合インデックスは、姓、名の順の電話帳。先頭の列から順に並ぶので、先頭を使って引く。姓で引けば直行。名だけで引くと、ふつうは並びを生かせない",
    "姓で引けば直行。名だけで引くと、ふつうは並びを生かせない。",
  ),
  "comprehension": glossaryVisualWithDark(
    "comprehension",
    "state",
    "内包表記は、元のリストから新しいリストを 1 行で作る書き方。選んで、変えて、新しい列に並べる。元の nums はそのまま残り、選んで変えた新しいリストができる",
    "元の nums はそのまま残り、選んで変えた新しいリストができる。",
  ),
  "conditional": glossaryVisualWithDark(
    "conditional",
    "flowchart",
    "条件分岐は、最初に合った道へ進む分岐器。if / elif / else は、上から順に調べて合ったら残りは見ない。合う道が 2 本あっても進むのは 1 本。だから並べる順番が大事",
    "合う道が 2 本あっても進むのは 1 本。だから並べる順番が大事。",
  ),
  "conditional-rendering": glossaryVisualWithDark(
    "conditional-rendering",
    "er",
    "条件付きレンダリングは、条件で画面に出す部品を選ぶこと。&& で書くと、偽のときは何も描かない。ただし 0 は描かれる。条件で出す部品を選ぶ。count && の書き方は 0 が画面に出る",
    "条件で出す部品を選ぶ。count && の書き方は 0 が画面に出る。",
  ),
  "conflict": glossaryVisualWithDark(
    "conflict",
    "timeline",
    "コンフリクトは、Git が人に聞いている印。同じ行を別々に直したときなどに起きる。故障ではない。印を消して残す形に直せば終わる",
    "故障ではない。印を消して残す形に直せば終わる。",
  ),
  "constructor": glossaryVisualWithDark(
    "constructor",
    "swimlane",
    "コンストラクタは、作るときに 1 度だけ動く係。new で呼ばれ、初期値を入れてから渡す。コンストラクタは、new のたびに 1 回動き、初期値を入れる",
    "コンストラクタは、new のたびに 1 回動き、初期値を入れる。",
  ),
  "container": glossaryVisualWithDark(
    "container",
    "quadrant",
    "コンテナは、核を共有する部屋。小さな仮想パソコンではなく、アプリと部品を詰めた箱。OS を丸ごと持たないから軽く、同じ箱を、環境がそろえば手元でも本番でも動かせる",
    "OS を丸ごと持たないから軽く、同じ箱を、環境がそろえば手元でも本番でも動かせる。",
  ),
  "content-type": glossaryVisualWithDark(
    "content-type",
    "dp-security-matrix",
    "Content-Type は、荷物に付ける中身の荷札。受け取った側は、札を手がかりに開ける。荷札が違うと、同じ中身でも扱いが変わる",
    "荷札が違うと、同じ中身でも扱いが変わる。",
  ),
  "context-engineering": glossaryVisualWithDark(
    "context-engineering",
    "high-level",
    "コンテキストエンジニアリングは、机に置く物を選ぶ設計。言い回しの上手さではなく、何を渡すかの設計。多いほど良いのではない。今の一手に要る物だけを置く",
    "多いほど良いのではない。今の一手に要る物だけを置く。",
  ),
  "contrast": glossaryVisualWithDark(
    "contrast",
    "nested",
    "コントラストは、地と字の明るさの比。色が違っても、明るさが近いと読めない。コントラストは、明るさの比。本文は 4.5 対 1 以上が目安",
    "コントラストは、明るさの比。本文は 4.5 対 1 以上が目安。",
  ),
  "control-structure": glossaryVisualWithDark(
    "control-structure",
    "tree",
    "制御構造は、処理の流れを決める 3 つの型。順番、分かれ道、くり返し。流れは、この 3 つの型の組み合わせで書ける",
    "流れは、この 3 つの型の組み合わせで書ける。",
  ),
  "control-unit": glossaryVisualWithDark(
    "control-unit",
    "org-chart",
    "制御装置は、命令を読み取って各装置に指示を出す。計算そのものは、演算装置の仕事。制御装置は指揮者。計算は演算装置、保存は記憶装置が行う",
    "制御装置は指揮者。計算は演算装置、保存は記憶装置が行う。",
  ),
  "convolutional-layer": glossaryVisualWithDark(
    "convolutional-layer",
    "layers",
    "畳み込み層は、小さな窓を滑らせて特徴を探す層。同じ窓の数字で、画像のどこでも探す。窓をずらして同じ計算を繰り返し、特徴の地図を作る",
    "窓をずらして同じ計算を繰り返し、特徴の地図を作る。",
  ),
  "cookie": glossaryVisualWithDark(
    "cookie",
    "sequence",
    "Cookie は、ブラウザが預かるクロークの札。利用者を区別する Cookie の例。札を見せると前と結びつく。前と結びつけられるのは、ブラウザが札を毎回見せるから",
    "前と結びつけられるのは、ブラウザが札を毎回見せるから。",
  ),
  "cors": glossaryVisualWithDark(
    "cors",
    "sequence",
    "CORS は、返事を通すか決めるブラウザの改札。止めるのはサーバーではなく、ブラウザ。許可の紙が無い返事を、ブラウザは JavaScript に見せない",
    "許可の紙が無い返事を、ブラウザは JavaScript に見せない。",
  ),
  "cosine-similarity": glossaryVisualWithDark(
    "cosine-similarity",
    "radar",
    "コサイン類似度は、2 本の針の向きの近さ。長さは見ない。向きが同じなら 1、直角なら 0。針が長いほど似ているのではなく、向きが近いほど似ている",
    "針が長いほど似ているのではなく、向きが近いほど似ている。",
  ),
  "counter": glossaryVisualWithDark(
    "counter",
    "venn",
    "カウンタは、手で押す数取り器。勝手に増えない。足す 1 行を書いて初めて増える。数えたい物が通っても、足す 1 行が無ければカウンタは動かない",
    "数えたい物が通っても、足す 1 行が無ければカウンタは動かない。",
  ),
  "coverage": glossaryVisualWithDark(
    "coverage",
    "pyramid",
    "カバレッジは、テストで動いた行の割合。100% でも、結果を確かめているとは限らない。動いたかどうかの数字で、確かめた数字ではない",
    "動いたかどうかの数字で、確かめた数字ではない。",
  ),
  "cross-validation": glossaryVisualWithDark(
    "cross-validation",
    "gantt",
    "5 分割の交差検証は、試験の束を入れ替えて測る。1 回の分け方の運を、平均でならす。平均だけでなく、点数のばらつきも見る",
    "平均だけでなく、点数のばらつきも見る。",
  ),
  "csp": glossaryVisualWithDark(
    "csp",
    "dp-security-matrix",
    "CSP は、警備員に渡す読み込みの許可名簿。名簿に無い所から来たスクリプトを、ブラウザが止める。名簿は穴をふさがない。エスケープと組む二重の守り",
    "名簿は穴をふさがない。エスケープと組む二重の守り。",
  ),
  "csrf": glossaryVisualWithDark(
    "csrf",
    "sequence",
    "CSRF は、秘書が印鑑を勝手に押してしまうこと。別のサイトの偽の依頼書にも、宛先の印鑑が押されうる。窓口は、推測できない確認番号も確かめる",
    "窓口は、推測できない確認番号も確かめる。",
  ),
  "css": glossaryVisualWithDark(
    "css",
    "layers",
    "CSS は、同じ中身に服を着せる。服を替えても、中身 (HTML) は変わらない。誰に着せるかをセレクターで選び、服の指定を替えれば見た目だけが変わる",
    "誰に着せるかをセレクターで選び、服の指定を替えれば見た目だけが変わる。",
  ),
  "css-variable": glossaryVisualWithDark(
    "css-variable",
    "radar",
    "CSS 変数は、1 か所で決めた値を名前で引く仕組み。--brand: green; と書き、var(--brand) で引く。1 か所の値を変えれば、それを var() で引いている所がそろって変わる",
    "1 か所の値を変えれば、それを var() で引いている所がそろって変わる。",
  ),
  "csv-parse": glossaryVisualWithDark(
    "csv-parse",
    "loop",
    "csv-parse は、引用符の中のカンマを区切りにしない。カンマで split すると、引用符の中まで切れてしまう。CSV では引用符の中のカンマは区切りではない。csv-parse がそれを読み分ける",
    "CSV では引用符の中のカンマは区切りではない。csv-parse がそれを読み分ける。",
  ),
  "curl": glossaryVisualWithDark(
    "curl",
    "data-lake",
    "curl は、ブラウザなしで HTTP の要求を出す道具。ブラウザが裏でやる要求を自分で出し、返事を文字で読める。画面になる前の返事をそのまま見られる",
    "画面になる前の返事をそのまま見られる。",
  ),
  "custom-hook": glossaryVisualWithDark(
    "custom-hook",
    "bar",
    "カスタムフックは、手順だけを共有し、state は共有しない。名前が use で始まる、自分で作る関数。同じフックを 2 か所で呼んでも、state は呼んだ所ごとに別々に持たれる",
    "同じフックを 2 か所で呼んでも、state は呼んだ所ごとに別々に持たれる。",
  ),
  "data-literacy": glossaryVisualWithDark(
    "data-literacy",
    "line",
    "データリテラシーは、グラフの数字を確かめて読む力。同じ数字でも、縦軸の切り方で別の話に見える。出どころ、軸の始点、件数を確かめてから読む",
    "出どころ、軸の始点、件数を確かめてから読む。",
  ),
  "data-structure": glossaryVisualWithDark(
    "data-structure",
    "gantt",
    "データ構造は、データの置き方。同じ A、B、C でも、置き方で取り出し方が変わる。置き方を選ぶのは、取り出し方を選ぶこと。用途で使い分ける",
    "置き方を選ぶのは、取り出し方を選ぶこと。用途で使い分ける。",
  ),
  "data-type": glossaryVisualWithDark(
    "data-type",
    "quadrant",
    "型は、数と文字を分ける種類。見た目が同じ 5 でも、足した結果が変わる。入力欄の値は文字で届く。数に直してから足す",
    "入力欄の値は文字で届く。数に直してから足す。",
  ),
  "database": glossaryVisualWithDark(
    "database",
    "nested",
    "データベースは、書庫と出し入れの係の組。置き場がデータベース、さばく係が DBMS。ふつうは 2 つ合わせて呼ぶ。棚には直接さわらず、係に頼む。同時の頼みも、係が整理してさばく",
    "棚には直接さわらず、係に頼む。同時の頼みも、係が整理してさばく。",
  ),
  "dataclass": glossaryVisualWithDark(
    "dataclass",
    "scatter",
    "dataclass は、項目を書くだけで道具が付く。型ヒントは書くが、実行時には検査されない。付くのは __init__ などの道具で、実行時の型の検査はしない",
    "付くのは __init__ などの道具で、実行時の型の検査はしない。",
  ),
  "db-t3-micro": glossaryVisualWithDark(
    "db-t3-micro",
    "medallion",
    "db.t3.micro は、機械の大きさの名前。DB の種類ではなく、RDS で借りる機械のサイズ。MySQL などの種類は別に選ぶ。名前はサイズの札",
    "MySQL などの種類は別に選ぶ。名前はサイズの札。",
  ),
  "debug": glossaryVisualWithDark(
    "debug",
    "state",
    "デバッグは、値の流れを点検窓でたどること。エラーが見える所と、原因の所は違うことがある。どこまで合っているかを狭めて、疑う場所を絞る",
    "どこまで合っているかを狭めて、疑う場所を絞る。",
  ),
  "decision": glossaryVisualWithDark(
    "decision",
    "high-level",
    "判断は、条件で進む道を 1 つに決める菱形の記号。出口は 2 つあるが、進めるのは 1 つだけ。条件の答えが、はいなら 1 本、いいえならもう 1 本を進む",
    "条件の答えが、はいなら 1 本、いいえならもう 1 本を進む。",
  ),
  "decisiontree": glossaryVisualWithDark(
    "decisiontree",
    "tree",
    "決定木は、質問を重ねて仕分ける道筋。AI が黙って決めた答えではなく、理由を道筋で読める。はい / いいえで分けた道筋が、そのまま答えの理由",
    "はい / いいえで分けた道筋が、そのまま答えの理由。",
  ),
  "deep-learning": glossaryVisualWithDark(
    "deep-learning",
    "nested",
    "深層学習は、層を深く重ねたもの。深いのは考えではなく、層の数。画像の例。層を重ねるほど、線から部品、全体を拾う",
    "画像の例。層を重ねるほど、線から部品、全体を拾う。",
  ),
  "default-argument": glossaryVisualWithDark(
    "default-argument",
    "process",
    "デフォルト引数は、定義のときに 1 回だけ作られる。[] を初期値にすると、呼び出しをまたいで同じ物を使う。省いた呼び出しは同じかごを使う。毎回新しくするなら None を初期値にする",
    "省いた呼び出しは同じかごを使う。毎回新しくするなら None を初期値にする。",
  ),
  "delivery": glossaryVisualWithDark(
    "delivery",
    "data-flow",
    "納品は、約束した形の成果物を依頼者へ引き渡すこと。流れの一例は、渡す、受領をもらう、検収を受ける。渡して終わりとは限らない。完了の条件は、契約で決める",
    "渡して終わりとは限らない。完了の条件は、契約で決める。",
  ),
  "dependencies": glossaryVisualWithDark(
    "dependencies",
    "dp-integration",
    "dependencies は、アプリが頼る部品をぶら下げた一覧。自分のコードの先に、他人の部品がぶら下がる。自分で書いたのは一部。npm install が先の先の部品まで入れる",
    "自分で書いたのは一部。npm install が先の先の部品まで入れる。",
  ),
  "dependency-array": glossaryVisualWithDark(
    "dependency-array",
    "high-level-parametric",
    "依存配列は、useEffect を走らせ直す値のリスト。並べた値が前回と違うときだけ、もう一度走る。前回と今回を見比べて、違ったときだけ再実行する。同じなら走らない",
    "前回と今回を見比べて、違ったときだけ再実行する。同じなら走らない。",
  ),
  "dependency-direction": glossaryVisualWithDark(
    "dependency-direction",
    "dp-security-matrix",
    "依存方向は、使う側から使われる側へ向く矢印。矢印は、データの流れではなく、誰が誰を知っているか。プラグが受け口の形に合わせる。受け口は、差す側を知らない",
    "プラグが受け口の形に合わせる。受け口は、差す側を知らない。",
  ),
  "deploy": glossaryVisualWithDark(
    "deploy",
    "process",
    "デプロイは、新しい箱に入れ替える作業。置くだけでは終わらない。確認して、だめなら前の箱へ戻す。梱包、設置、確認までがデプロイ。前の版は残しておく",
    "梱包、設置、確認までがデプロイ。前の版は残しておく。",
  ),
  "derived-table": glossaryVisualWithDark(
    "derived-table",
    "it-current-state",
    "導出テーブルは、その場だけの黒板。FROM の括弧の中は、問い合わせの間だけ使うメモ。名前 (AS) を付けて外側から読む。終わると消えて、台帳は増えない",
    "名前 (AS) を付けて外側から読む。終わると消えて、台帳は増えない。",
  ),
  "design": glossaryVisualWithDark(
    "design",
    "sequence-oauth",
    "設計は、作りとつなぎを先に考えること。図にすると整理しやすい。作る前に考えると、大きな手戻りを減らせる。作って分かったことは図に戻す",
    "作る前に考えると、大きな手戻りを減らせる。作って分かったことは図に戻す。",
  ),
  "devdependencies": glossaryVisualWithDark(
    "devdependencies",
    "architecture",
    "devDependencies は、本番に送らない道具。テストやビルドの道具は工場に置き、出荷の箱には入れない。本番で動く部品だけを dependencies に書く",
    "本番で動く部品だけを dependencies に書く。",
  ),
  "directory": glossaryVisualWithDark(
    "directory",
    "flowchart",
    "ディレクトリは、ファイルをしまう入れ子の引き出し。フォルダと同じ物。中にさらに入れられる。ディレクトリは、フォルダの別名。cd で入り、ls で中身を見る",
    "ディレクトリは、フォルダの別名。cd で入り、ls で中身を見る。",
  ),
  "divide-and-conquer": glossaryVisualWithDark(
    "divide-and-conquer",
    "sequence",
    "分割統治法は、分けて解いて合わせる。分けるだけでは終わらない。分ける、小さく解く、答えを合わせる。この 3 手で大きな問題を解く",
    "分ける、小さく解く、答えを合わせる。この 3 手で大きな問題を解く。",
  ),
  "do-while": glossaryVisualWithDark(
    "do-while",
    "state",
    "do-while は、先にやってから条件を見る。最低 1 回は、必ず動く。do-while は、最初の条件判定の前に、本体が必ず 1 回動く",
    "do-while は、最初の条件判定の前に、本体が必ず 1 回動く。",
  ),
  "docker": glossaryVisualWithDark(
    "docker",
    "layers",
    "Docker は、同じ箱を持ち運ぶ道具。OS ごとではなく、アプリと必要なものだけを箱に詰める。コンテナは、イメージから起動した 1 つの箱。核は共有する",
    "コンテナは、イメージから起動した 1 つの箱。核は共有する。",
  ),
  "dockerfile": glossaryVisualWithDark(
    "dockerfile",
    "er",
    "Dockerfile は、イメージを作る手順書。コンテナそのものではなく、作り方を書いたレシピ。build で手順書からイメージ、run でイメージからコンテナ",
    "build で手順書からイメージ、run でイメージからコンテナ。",
  ),
  "dom": glossaryVisualWithDark(
    "dom",
    "tree",
    "DOM は、HTML の構造をブラウザが組んだ模型。JavaScript が触るのは、ファイルでなく模型。模型を直してもファイルは元のまま。再読み込みすれば、ファイルから組み直される",
    "模型を直してもファイルは元のまま。再読み込みすれば、ファイルから組み直される。",
  ),
  "domain": glossaryVisualWithDark(
    "domain",
    "timeline",
    "ドメインは、サーバーの番号に掛ける表札。名前は借りる物。番号は変わることがある。引っ越しても、表札 (ドメイン) は使い続けられる",
    "引っ越しても、表札 (ドメイン) は使い続けられる。",
  ),
  "draw-io": glossaryVisualWithDark(
    "draw-io",
    "swimlane",
    "draw.io は、箱と線がつながりを覚えている製図板。線の端が箱についているから、箱を動かすと線が追う。ブラウザで無料で使え、.drawio のファイルで保存して共有できる",
    "ブラウザで無料で使え、.drawio のファイルで保存して共有できる。",
  ),
  "dynamic-programming": glossaryVisualWithDark(
    "dynamic-programming",
    "quadrant",
    "動的計画法は、小さな問題の答えをメモして使い回す手法。フィボナッチ数 f(n) = f(n - 1) + f(n - 2) で見る。答えを 1 度ノートに書けば、同じ計算を繰り返さずに済む",
    "答えを 1 度ノートに書けば、同じ計算を繰り返さずに済む。",
  ),
  "ecosystem": glossaryVisualWithDark(
    "ecosystem",
    "nested",
    "エコシステムは、言語の周りの部品と道具と仲間。言語の本体だけを指す言葉ではない。本体の外にある部品、道具、情報までを含めて、言語の強さが決まる",
    "本体の外にある部品、道具、情報までを含めて、言語の強さが決まる。",
  ),
  "edge-case": glossaryVisualWithDark(
    "edge-case",
    "tree",
    "エッジケースは、入力の範囲の端。真ん中だけ試すと、端で落ちる失敗を見逃す。0 件や最大のような端でも動くか。そこを先に試す",
    "0 件や最大のような端でも動くか。そこを先に試す。",
  ),
  "encapsulation": glossaryVisualWithDark(
    "encapsulation",
    "org-chart",
    "カプセル化は、窓口を通して中の決まりを守らせる。隠すことが目的ではなく、勝手に書き換えさせないこと。自動販売機は、投入口とボタンを通した操作しか受けつけない",
    "自動販売機は、投入口とボタンを通した操作しか受けつけない。",
  ),
  "endpoint": glossaryVisualWithDark(
    "endpoint",
    "layers",
    "endpoint は、メソッドと URL の組でできた窓口。Web API では、同じ URL でも GET と POST は別の窓口。URL だけでは窓口は決まらない。メソッドも見て振り分ける",
    "URL だけでは窓口は決まらない。メソッドも見て振り分ける。",
  ),
  "entity": glossaryVisualWithDark(
    "entity",
    "venn",
    "エンティティは、ER 図で管理したい物事の種類を表す見出し札。顧客や注文のように、設計の最初に決めるもの。エンティティは物事の種類。ふつう表 1 つで管理し、1 件が 1 行",
    "エンティティは物事の種類。ふつう表 1 つで管理し、1 件が 1 行。",
  ),
  "entry-point": glossaryVisualWithDark(
    "entry-point",
    "pyramid",
    "エントリーポイントは、建物の正面玄関。定義した関数が全部動くのではなく、入口から呼ばれて動く。入口から呼ばれた関数が動き、呼ばれない関数の中身は動かない",
    "入口から呼ばれた関数が動き、呼ばれない関数の中身は動かない。",
  ),
  "environment-variable": glossaryVisualWithDark(
    "environment-variable",
    "radar",
    "環境変数は、レシピに書かない秘伝のたれ。コードには名前だけ書き、値は動かす場所ごとに置く。鍵は履歴に残さない。漏れたら失効させて交換する",
    "鍵は履歴に残さない。漏れたら失効させて交換する。",
  ),
  "error-message": glossaryVisualWithDark(
    "error-message",
    "loop",
    "エラーメッセージは、どこで何が起きたかの診断票。怖い赤字ではなく、直す手がかりが書いてある。最後の行が「何が」、上の行が「どこで」。読めば直す場所が分かる",
    "最後の行が「何が」、上の行が「どこで」。読めば直す場所が分かる。",
  ),
  "escape": glossaryVisualWithDark(
    "escape",
    "data-lake",
    "エスケープは、特別な字をただの字として使う印。ここでは引用符の例。\\n のように別の意味を足す使い方もある。囲みの記号を文字として使うときは、前に \\ を付ける。画面には出ない",
    "囲みの記号を文字として使うときは、前に \\ を付ける。画面には出ない。",
  ),
  "evaluation": glossaryVisualWithDark(
    "evaluation",
    "radar",
    "評価は、1 つの数字だけで決めない。100 個を検品して、不良 (陽性) は 1 個だけの場合。正解率が高くても、少数の見逃しは隠れる",
    "正解率が高くても、少数の見逃しは隠れる。",
  ),
  "event": glossaryVisualWithDark(
    "event",
    "sequence",
    "イベントは、呼び鈴と「出る係」の組。鳴らすだけでは動かない。出る係の登録が要る。押す、鳴る、登録した関数が動く。登録が無ければ、誰も出ない",
    "押す、鳴る、登録した関数が動く。登録が無ければ、誰も出ない。",
  ),
  "event-loop": glossaryVisualWithDark(
    "event-loop",
    "bar",
    "イベントループは、1 人の料理人と伝票レール。同時に動かすのではなく、1 枚ずつ順番にこなす (タイマーの例)。待ち時間は外の係に任せる。終わると、後の処理が列の最後に並ぶ",
    "待ち時間は外の係に任せる。終わると、後の処理が列の最後に並ぶ。",
  ),
  "event-preventdefault": glossaryVisualWithDark(
    "event-preventdefault",
    "line",
    "event.preventDefault は、ブラウザの既定の動きを止める。イベントそのものは止まらない。止まるのは既定の動きだけで、イベントは親へ伝わる",
    "止まるのは既定の動きだけで、イベントは親へ伝わる。",
  ),
  "exception-handling": glossaryVisualWithDark(
    "exception-handling",
    "gantt",
    "例外処理は、綱渡りの下の安全ネット。エラーは起きる前提で、落ちたときの道を用意する。受け止めたら理由を残す。黙って捨てると原因が分からない",
    "受け止めたら理由を残す。黙って捨てると原因が分からない。",
  ),
  "express-js": glossaryVisualWithDark(
    "express-js",
    "scatter",
    "Express.js は、Node.js の上で動くサーバー用の道具箱。別の言語でも、Node.js の代わりでもない。Node.js が土台で、その上で道ごとの担当を札で決める",
    "Node.js が土台で、その上で道ごとの担当を札で決める。",
  ),
  "express-json": glossaryVisualWithDark(
    "express-json",
    "medallion",
    "express.json() は、箱を開ける係。通さないと、req.body は undefined のまま。処理より前に置いて通すと、application/json の本文が開梱されて req.body に入る",
    "処理より前に置いて通すと、application/json の本文が開梱されて req.body に入る。",
  ),
  "f-string": glossaryVisualWithDark(
    "f-string",
    "high-level",
    "f-string は、{ } の穴に値を入れる文字列。文字列の頭に f を付けたときだけ。f を付けない文字列は、{ } がそのまま出る",
    "f を付けない文字列は、{ } がそのまま出る。",
  ),
  "fetch-api": glossaryVisualWithDark(
    "fetch-api",
    "flowchart",
    "fetch は、呼び出しブザーを先に渡す。答えは後から来る。届いた箱は、自分で開ける。404 でも箱は届く。ok を見て、成功かを確かめる",
    "404 でも箱は届く。ok を見て、成功かを確かめる。",
  ),
  "fetch-api-2": glossaryVisualWithDark(
    "fetch-api-2",
    "process",
    "Fetch API は、ブラウザに最初から入っている道具箱。fetch() は道具の 1 つで、Request や Response などの部品もそろっている。インストール不要で、fetch() は結果を Promise に包んで返す",
    "インストール不要で、fetch() は結果を Promise に包んで返す。",
  ),
  "field": glossaryVisualWithDark(
    "field",
    "data-flow",
    "フィールドは、オブジェクトが持つ引き出し。メソッドの中の変数は、呼び終わると消える。フィールドは残り、メソッドの中の変数は呼び終わると消える",
    "フィールドは残り、メソッドの中の変数は呼び終わると消える。",
  ),
  "flex-wrap": glossaryVisualWithDark(
    "flex-wrap",
    "dp-integration",
    "flex-wrap は、収まらない物を折り返すかを決める。既定は折り返さず、横 1 行のまま。足りなくても勝手には折り返さない。wrap と書いて初めて次の段へ回る",
    "足りなくても勝手には折り返さない。wrap と書いて初めて次の段へ回る。",
  ),
  "flowchart": glossaryVisualWithDark(
    "flowchart",
    "high-level-parametric",
    "フローチャートは、形に意味がある手順の地図。どの形で書いてもよい図ではなく、形が役割を表す。ひし形の出口は、条件ごとの道。多くは「はい」と「いいえ」の 2 本",
    "ひし形の出口は、条件ごとの道。多くは「はい」と「いいえ」の 2 本。",
  ),
  "form": glossaryVisualWithDark(
    "form",
    "dp-security-matrix",
    "フォームは、記入欄をまとめて送る申込用紙。標準の送信では、name のある欄をまとめて送る。name の無い欄は、書いても送られない",
    "name の無い欄は、書いても送られない。",
  ),
  "forward-propagation": glossaryVisualWithDark(
    "forward-propagation",
    "layers",
    "順伝播は、入力から出力へ計算を流すだけ。この間、つまみ (重み) は 1 つも動かない。学習では、順伝播のあとに誤差を測り、逆伝播で勾配を求めて直す",
    "学習では、順伝播のあとに誤差を測り、逆伝播で勾配を求めて直す。",
  ),
  "fragment": glossaryVisualWithDark(
    "fragment",
    "it-current-state",
    "Fragment は、中身だけ渡す透明なクリアファイル。DOM に要素を足さず、複数の子をまとめて返す袋。1 つにまとめて返しつつ、余計な要素を DOM に足さない",
    "1 つにまとめて返しつつ、余計な要素を DOM に足さない。",
  ),
  "framework": glossaryVisualWithDark(
    "framework",
    "sequence-oauth",
    "フレームワークは、呼ぶ側が逆になる骨組み。ライブラリは自分が呼ぶ。骨組みは、自分のコードを呼ぶ。ライブラリは、自分が呼ぶ道具。フレームワークは、自分を呼ぶ骨組み",
    "ライブラリは、自分が呼ぶ道具。フレームワークは、自分を呼ぶ骨組み。",
  ),
  "frontend": glossaryVisualWithDark(
    "frontend",
    "architecture",
    "フロントエンドの確認は、親切であって守りではない。手元の画面は利用者のもの。書き換えられ、飛ばされる。窓口 (サーバー) でも、必ず確かめる",
    "窓口 (サーバー) でも、必ず確かめる。",
  ),
  "full-scan": glossaryVisualWithDark(
    "full-scan",
    "architecture",
    "フルスキャンは、全行を端から読むこと。WHERE を書いても、索引が無いと全部読む。合わない行は、読んだあとに結果から外される。答えが 3 行でも読むのは全部",
    "合わない行は、読んだあとに結果から外される。答えが 3 行でも読むのは全部。",
  ),
  "fullstack": glossaryVisualWithDark(
    "fullstack",
    "flowchart",
    "フルスタックは、食堂の全部の部屋。客席から倉庫まで、一通り作れる人。3 つの部屋をまたいで作れる人。全部が一番の腕とは限らない",
    "3 つの部屋をまたいで作れる人。全部が一番の腕とは限らない。",
  ),
  "function": glossaryVisualWithDark(
    "function",
    "sequence",
    "関数は、レシピカードの箱。しまっただけでは、何も作られない。呼んだときに動く。材料を変えて、何度でも呼べる",
    "呼んだときに動く。材料を変えて、何度でも呼べる。",
  ),
  "function-definition": glossaryVisualWithDark(
    "function-definition",
    "sequence",
    "関数定義は、名前と受け口を決めて処理を書くこと。受け口の fruit には、呼ばれたときに値が入る。def は機械を作るだけ。呼ぶたびに、渡した値を受け口に入れて結果を返す",
    "def は機械を作るだけ。呼ぶたびに、渡した値を受け口に入れて結果を返す。",
  ),
  "functional-dependency": glossaryVisualWithDark(
    "functional-dependency",
    "state",
    "関数従属は、A が決まれば B も 1 つに決まる関係。学籍番号は学生を 1 人ずつ区別する。同じ番号なら同じ学生なので氏名も決まる。学籍番号が同じなら氏名も同じ。でも、氏名が同じでも学籍番号は同じとは限らない",
    "学籍番号が同じなら氏名も同じ。でも、氏名が同じでも学籍番号は同じとは限らない。",
  ),
  "generative-ai": glossaryVisualWithDark(
    "generative-ai",
    "org-chart",
    "生成 AI は、見て覚えた傾向から新しく作る。元の絵を切り貼りしているのではない。持っているのは傾向の札。だから同じ頼みでも、ふつう毎回ちがう",
    "持っているのは傾向の札。だから同じ頼みでも、ふつう毎回ちがう。",
  ),
  "generics": glossaryVisualWithDark(
    "generics",
    "er",
    "ジェネリクスは、荷札で入る物を決める箱。List<String> に String 以外を足すと、コンパイルエラー。型を書いた List なら、型の間違いは動かす前に見つかる",
    "型を書いた List なら、型の間違いは動かす前に分かる。",
  ),
  "git": glossaryVisualWithDark(
    "git",
    "state",
    "Git は、コミットで撮る写真のアルバム。保存しただけでは、アルバムは増えない。commit で、add した時点の内容が残る。撮るまでは記録されない",
    "commit で、add した時点の内容が残る。撮るまでは記録されない。",
  ),
  "git-clone": glossaryVisualWithDark(
    "git-clone",
    "timeline",
    "clone は、アルバムごと写しを手元に作る。ZIP のダウンロードとは、履歴が付くかどうかが違う。履歴つきの写しと、取得元の名前 (origin) が手元に残る",
    "履歴つきの写しと、取得元の名前 (origin) が手元に残る。",
  ),
  "git-pull": glossaryVisualWithDark(
    "git-pull",
    "swimlane",
    "pull は、取り寄せてから手元へ合わせる 2 手。書庫の新しい写真を、手元のアルバムに足す。pull は fetch と取り込みの 2 手。書庫は変わらない",
    "pull は fetch と取り込みの 2 手。書庫は変わらない。",
  ),
  "github": glossaryVisualWithDark(
    "github",
    "swimlane",
    "GitHub は、アルバムの写しを置いて共有する書庫。Git は手元の道具。GitHub はその置き場の 1 つ。書庫が無くても Git は使える。GitLab など別の書庫もある",
    "書庫が無くても Git は使える。GitLab など別の書庫もある。",
  ),
  "graph": glossaryVisualWithDark(
    "graph",
    "quadrant",
    "グラフは、点と線のつながり。路線図のように、つながりを表すデータ。棒グラフとは別物。つながりをたどって探せる",
    "棒グラフとは別物。つながりをたどって探せる。",
  ),
  "guardrails": glossaryVisualWithDark(
    "guardrails",
    "layers",
    "Guardrails は、AI の入口と出口の遮断機。善悪を教え込むのではなく、規則の関所で止める。すり抜けることもある。1 つに頼らず、関所を重ねる",
    "すり抜けることもある。1 つに頼らず、関所を重ねる。",
  ),
  "hash-function": glossaryVisualWithDark(
    "hash-function",
    "nested",
    "ハッシュ関数は、戻せない肉挽き機。SHA-256 のような暗号用は、値から元へ逆算するのが極めて難しい。暗号化とは別物。暗号用のハッシュは元に戻すのが極めて難しい",
    "暗号化とは別物。暗号用のハッシュは元に戻すのが極めて難しい。",
  ),
  "hash-table": glossaryVisualWithDark(
    "hash-table",
    "tree",
    "ハッシュテーブルは、計算で棚を開けるロッカーの壁。キーから棚番号を計算し、その棚を開けて値を取り出す。キーから棚番号を計算し、その棚から探して値を取り出す",
    "キーから棚番号を計算し、その棚から探して値を取り出す。",
  ),
  "hashing": glossaryVisualWithDark(
    "hashing",
    "org-chart",
    "ハッシュ化は、戻せないひき肉にすること。暗号化は鍵で戻せる。ハッシュ化は戻せない。戻せないから、保存した値と比べて確かめる",
    "戻せないから、保存した値と比べて確かめる。",
  ),
  "hashmap": glossaryVisualWithDark(
    "hashmap",
    "layers",
    "HashMap は、番号札のロッカー。名前から場所を計算して、直接開ける。名前から場所が決まるから、全部を探さずに取り出せる",
    "名前から場所が決まるから、全部を探さずに取り出せる。",
  ),
  "hashset": glossaryVisualWithDark(
    "hashset",
    "venn",
    "HashSet は、番号ロッカーの名簿。同じ物は 1 つだけ。入っているかをすぐ調べられる。同じ物は増えない。入っているかは、番号の扉を見に行くだけで調べられる",
    "同じ物は増えない。入っているかは、番号の扉を見に行くだけで調べられる。",
  ),
  "health-check": glossaryVisualWithDark(
    "health-check",
    "pyramid",
    "ヘルスチェックは、店の営業中の確認。決まった URL へ定期的に問い合わせ、正常でない店を外す。正常な店へお客を回す。失敗が続けば外し、戻れば入れ直す",
    "正常な店へお客を回す。失敗が続けば外し、戻れば入れ直す。",
  ),
  "heap": glossaryVisualWithDark(
    "heap",
    "radar",
    "ヒープは、親が子より大きくならない木。全体が小さい順に並んでいるわけではない。頂上は最小。兄弟どうしの順は決まっていない",
    "頂上は最小。兄弟どうしの順は決まっていない。",
  ),
  "hosted-zone": glossaryVisualWithDark(
    "hosted-zone",
    "loop",
    "ホストゾーンは、1 つのドメインの記録をまとめた帳面。サーバーの置き場ではなく、名前と行き先の帳面。記録をまとめた帳面。買った所が別なら NS を伝える",
    "記録をまとめた帳面。買った所が別なら NS を伝える。",
  ),
  "hot-reload": glossaryVisualWithDark(
    "hot-reload",
    "data-lake",
    "ホットリロードは、動かしたまま変更だけを入れ替える。画面の数 (状態) は、そのまま残る。保存すると変更だけが入り、5 は残る。最初からやり直すのは hot restart",
    "保存すると変更だけが入り、5 は残る。最初からやり直すのは hot restart。",
  ),
  "hover": glossaryVisualWithDark(
    "hover",
    "bar",
    "ホバーは、押さずにポインタを乗せるだけの操作。指でさわる画面には、乗せるだけの動きが無い。ホバーだけに頼った説明やメニューは、スマホでは見えない",
    "ホバーだけに頼った説明やメニューは、スマホでは見えない。",
  ),
  "html": glossaryVisualWithDark(
    "html",
    "nested",
    "HTML は、文章に役目の札を付ける。見た目と動きは、主に別の道具の仕事。札は意味を伝える。色や形は主に CSS、動きは主に JavaScript が受け持つ",
    "札は意味を伝える。色や形は主に CSS、動きは主に JavaScript が受け持つ。",
  ),
  "http": glossaryVisualWithDark(
    "http",
    "flowchart",
    "HTTP は、往復はがきのやり取り。頼まれたら返す。それが 1 往復。ページ 1 枚でも、画像などを別に頼むので往復が増える",
    "ページ 1 枚でも、画像などを別に頼むので往復が増える。",
  ),
  "http-method": glossaryVisualWithDark(
    "http-method",
    "line",
    "HTTP メソッドは、窓口に出す伝票のハンコ。何をしたいかの宣言。暗号化は GET も POST も HTTPS の役目。GET は取得だけの頼み。POST は 2 回出すと、処理が重なることがある",
    "GET は取得だけの頼み。POST は 2 回出すと、処理が重なることがある。",
  ),
  "http-request": glossaryVisualWithDark(
    "http-request",
    "gantt",
    "リクエストは、URL だけでなく用紙 1 枚。HTTP/1.1 では、1 行目、ヘッダー、空行、ボディの順に書く。1 行目が「何を、どこへ」。残りは付け足しと中身",
    "1 行目が「何を、どこへ」。残りは付け足しと中身。",
  ),
  "httpd": glossaryVisualWithDark(
    "httpd",
    "scatter",
    "httpd は、HTTP の要求を待ち受けて返す常駐プログラム。機械の名前ではなく、Apache の実行ファイルの名前。d は daemon の d。マシンの上で動き続ける、受付係のプログラム",
    "d は daemon の d。マシンの上で動き続ける、受付係のプログラム。",
  ),
  "httpsession": glossaryVisualWithDark(
    "httpsession",
    "architecture",
    "HttpSession は、サーバー側の預かり所。ブラウザが持つのは券だけ。中身はサーバー側。有効な券 (ID) を見せれば同じ預かりに戻れる。なければ必要に応じて新しく作られる",
    "有効な券 (ID) を見せれば同じ預かりに戻れる。なければ必要に応じて新しく作られる。",
  ),
  "human-in-the-loop": glossaryVisualWithDark(
    "human-in-the-loop",
    "sequence",
    "Human-in-the-loop は、大事な所に人の判子を置くこと。全部やり直すのではなく、要所で人が確かめる。AI は下書き、人が承認。印の付いた物だけ先へ進む",
    "AI は下書き、人が承認。印の付いた物だけ先へ進む。",
  ),
  "hypothesis": glossaryVisualWithDark(
    "hypothesis",
    "medallion",
    "仮説は、試して確かめられる形の仮の答え。もし〜なら〜と書き、結果で残すか書き直す。正しいと決めた答えではなく、試すための仮の答え。確かめて、見直す",
    "正しいと決めた答えではなく、試すための仮の答え。確かめて、見直す。",
  ),
  "image": glossaryVisualWithDark(
    "image",
    "high-level",
    "イメージは、コンテナを作る読み取り専用の型。動いているのはイメージではなく、そこから起動したコンテナ。型は 1 つ。焼いたクッキー (コンテナ) は何個でも作れる",
    "型は 1 つ。焼いたクッキー (コンテナ) は何個でも作れる。",
  ),
  "immutable": glossaryVisualWithDark(
    "immutable",
    "process",
    "イミュータブルは、書き換えられない印刷の札。変えたいときは、元の札はそのままで、新しい札ができる。str は書き換えず、新しい値ができる。元の s は abc のまま",
    "str は書き換えず、新しい値ができる。元の s は abc のまま。",
  ),
  "immutable-update": glossaryVisualWithDark(
    "immutable-update",
    "data-flow",
    "不変更新は、元を書き換えず変えた写しを作ること。元の物は残り、変えた新しい物のほうを使う。元は触らず、変えた写しを新しく作る",
    "元は触らず、変えた写しを新しく作る。",
  ),
  "increment": glossaryVisualWithDark(
    "increment",
    "dp-integration",
    "インクリメントは、i 自体を 1 増やす。i + 1 は計算するだけで、i は変わらない。i++ は増やす前の値、++i は増やした後の値を式に渡す",
    "i++ は増やす前の値、++i は増やした後の値を式に渡す。",
  ),
  "indent": glossaryVisualWithDark(
    "indent",
    "high-level-parametric",
    "字下げは、どの行が「中」かを決める。目次と同じ。ずれると、動きが変わる。Python は字下げで「中」を決める。1 段ずれると結果が変わる",
    "Python は字下げで「中」を決める。1 段ずれると結果が変わる。",
  ),
  "indexerror": glossaryVisualWithDark(
    "indexerror",
    "dp-security-matrix",
    "IndexError は、無い番号の口を指したときの知らせ。Python のリストの番号は 0 から数える。5 個入りの最後は 4 番。5 番を指すと IndexError",
    "5 個入りの最後は 4 番。5 番を指すと IndexError。",
  ),
  "inheritance": glossaryVisualWithDark(
    "inheritance",
    "it-current-state",
    "継承は、親の上に重ねる透明シート。Python の継承。書いた所だけが上書きされる。子は親のコピーではない。上書きしない所は、親を直せば届く",
    "子は親のコピーではない。上書きしない所は、親を直せば届く。",
  ),
  "inline-code": glossaryVisualWithDark(
    "inline-code",
    "sequence-oauth",
    "インラインコードは、文の中に貼る等幅のラベル。囲んでも、動くわけではない。文の途中のコードを見分けやすくする表示。実行はされない",
    "文の途中のコードを見分けやすくする表示。実行はされない。",
  ),
  "innerhtml": glossaryVisualWithDark(
    "innerhtml",
    "architecture",
    "innerHTML は、渡した文字を HTML として読み、要素にする。同じ文字でも、textContent ならそのまま貼られる。入力に innerHTML を使うと、紛れ込んだタグが動く。ただの文字は textContent",
    "入力に innerHTML を使うと、紛れ込んだタグが動く。ただの文字は textContent。",
  ),
  "input-device": glossaryVisualWithDark(
    "input-device",
    "flowchart",
    "入力装置は、外の出来事を信号に変えて本体へ渡す機器。キーボードとマウスだけではない。押す、動かす、話す、映す。どれも外の出来事を信号にする",
    "押す、動かす、話す、映す。どれも外の出来事を信号にする。",
  ),
  "instance": glossaryVisualWithDark(
    "instance",
    "sequence",
    "インスタンスは、様式から刷った 1 枚ずつの会員証。1 枚を直しても、ほかの 1 枚は変わらない。インスタンスは、クラスから作った 1 つ 1 つ。それぞれが自分の値を持つ",
    "インスタンスは、クラスから作った 1 つ 1 つ。それぞれが自分の値を持つ。",
  ),
  "integer-parseint": glossaryVisualWithDark(
    "integer-parseint",
    "state",
    "Integer.parseInt は、文字の数字を数に変える。整数に読めない文字は、例外が投げられる。\"42\" は文字、42 は数。変えるのが parseInt",
    "\"42\" は文字、42 は数。変えるのが parseInt。",
  ),
  "interface": glossaryVisualWithDark(
    "interface",
    "architecture",
    "インターフェースは、口の約束だけを決める。壁のコンセントと同じ。中身は、挿した側が持つ。Java の interface は呼び方の約束。動きは、挿した側が決める",
    "Java の interface は呼び方の約束。動きは、挿した側が決める。",
  ),
  "interface-type": glossaryVisualWithDark(
    "interface-type",
    "er",
    "インターフェースは、コンセントの穴の形。決めるのは差せる形だけで、中身は差す側が持つ。interface は、約束の形だけを書く。形が合わない物は差せない",
    "interface は、約束の形だけを書く。形が合わない物は差せない。",
  ),
  "interface-type-2": glossaryVisualWithDark(
    "interface-type-2",
    "timeline",
    "type は、形にも組み合わせにも名前を付ける。interface と並べる。後から足せるのは interface、A または B に名付けられるのは type",
    "後から足せるのは interface、A または B に名付けられるのは type。",
  ),
  "internet": glossaryVisualWithDark(
    "internet",
    "layers",
    "インターネットは、道。Web はその上を走る車。メールも通話も、同じ道を別の車で走る。ブラウザで見る Web は、インターネットの上のサービスの 1 つ",
    "ブラウザで見る Web は、インターネットの上のサービスの 1 つ。",
  ),
  "interpreter": glossaryVisualWithDark(
    "interpreter",
    "swimlane",
    "インタプリタは、台本を上から順に実行する人。実行した所までは、もう結果が出ている。上から順に実行する。止まるのは、エラーの行に着いたとき",
    "上から順に実行する。止まるのは、エラーの行に着いたとき。",
  ),
  "io": glossaryVisualWithDark(
    "io",
    "quadrant",
    "入出力は、プログラムと外の世界のあいだの値の出入り。入ってくるのが入力、出ていくのが出力。相手が何でも、入ってくれば入力、出ていけば出力",
    "相手が何でも、入ってくれば入力、出ていけば出力。",
  ),
  "ioexception": glossaryVisualWithDark(
    "ioexception",
    "nested",
    "IOException は、入出力の失敗を知らせる例外。コードが正しくても、入出力は失敗しうる。コードが正しくても起きる。だから catch か throws を書かせる",
    "コードが正しくても起きる。だから catch か throws を書かせる。",
  ),
  "iterable": glossaryVisualWithDark(
    "iterable",
    "tree",
    "リストなどのイテラブルは、栞を何本でも挟める本。本は何度でも読める。iter() で挟んだ栞 (イテレータ) は一度きり。本は何度でも読める。栞は一度きり",
    "本は何度でも読める。栞は一度きり。",
  ),
  "iteration": glossaryVisualWithDark(
    "iteration",
    "org-chart",
    "イテレーションは、ループの 1 周のこと。ループ全体の名前ではなく、1 周ごとの数え方。3 周まわれば 3 回のイテレーション。0 周で終わることもある",
    "3 周まわれば 3 回のイテレーション。0 周で終わることもある。",
  ),
  "java": glossaryVisualWithDark(
    "java",
    "layers",
    "Java は、JavaScript とは別の言語。名前が似ているだけ。動く場所も書き方も違う。Java と JavaScript は、名前が似ているだけの別の言語",
    "Java と JavaScript は、名前が似ているだけの別の言語。",
  ),
  "javascript": glossaryVisualWithDark(
    "javascript",
    "sequence",
    "JavaScript は、押されたらの配線。ボタンを置くだけでは、チャイムは鳴らない。「押されたらこうする」をつなぐのが JavaScript。この例では鳴らす処理",
    "「押されたらこうする」をつなぐのが JavaScript。この例では鳴らす処理。",
  ),
  "json": glossaryVisualWithDark(
    "json",
    "nested",
    "JSON は、文字で書いた伝票。言語が違っても、文字なら受け渡せる。JSON はコードではなく文字。書き方の決まりは厳しい",
    "JSON はコードではなく文字。書き方の決まりは厳しい。",
  ),
  "json-stringify": glossaryVisualWithDark(
    "json-stringify",
    "venn",
    "JSON.stringify は、オブジェクトを 1 本の文字列にする。返ってくるのは JSON の形をした文字列で、オブジェクトではない。送る前に文字列にして、受け取った側が JSON.parse で読み直す",
    "送る前に文字列にして、受け取った側が JSON.parse で読み直す。",
  ),
  "jsonify": glossaryVisualWithDark(
    "jsonify",
    "pyramid",
    "jsonify は、JSON の荷札を付けた返事を作る。dumps は文字列だけ。荷札 (Content-Type) は jsonify が付ける。jsonify は文字列にし、application/json の荷札も付ける",
    "jsonify は文字列にし、application/json の荷札も付ける。",
  ),
  "junction-table": glossaryVisualWithDark(
    "junction-table",
    "radar",
    "中間テーブルは、多対多の組み合わせを 1 行ずつ持つ表。受講生と講座のように、互いに複数とつながる関係の橋渡し。組み合わせ 1 組が 1 行。列に詰めず、行で持つ",
    "組み合わせ 1 組が 1 行。列に詰めず、行で持つ。",
  ),
  "justify-content": glossaryVisualWithDark(
    "justify-content",
    "loop",
    "justify-content は、並ぶ向きの余りの配り方。Flexbox で左から右へ並ぶとき、決めるのは余った幅の行き先。文字の両端そろえとは別物。余りを左に残すか、間に配るかを決める",
    "文字の両端そろえとは別物。余りを左に残すか、間に配るかを決める。",
  ),
  "jwt": glossaryVisualWithDark(
    "jwt",
    "layers",
    "署名つき JWT は、窓つき封筒に印を押したもの。印は改ざんを見つける。中身を隠す物ではない。暗号化ではなく署名。秘密は入れない",
    "暗号化ではなく署名。秘密は入れない。",
  ),
  "jwt-sign": glossaryVisualWithDark(
    "jwt-sign",
    "data-lake",
    "jwt.sign は、中身に印を押す処理。暗号化ではなく、書き換えを見抜くための署名。中身は誰でも読める。印は、秘密を持つ側だけが押せる",
    "中身は誰でも読める。印は、秘密を持つ側だけが押せる。",
  ),
  "jwt-verify": glossaryVisualWithDark(
    "jwt-verify",
    "bar",
    "jwt.verify は、印を照合して通す処理。中身を読むだけの decode とは別。偽物と期限切れを弾く。印が合えば中身が返る。exp があれば期限も見て、だめなら例外",
    "印が合えば中身が返る。exp があれば期限も見て、だめなら例外。",
  ),
  "kebab-case": glossaryVisualWithDark(
    "kebab-case",
    "line",
    "ケバブケースは、単語を - の串でつなぐ書き方。URL や CSS で使う。変数名には使えない。kebab-case は、ハイフンでつなぐ。変数名には使えない",
    "kebab-case は、ハイフンでつなぐ。変数名には使えない。",
  ),
  "kernel": glossaryVisualWithDark(
    "kernel",
    "gantt",
    "カーネルは、アプリと機械の窓口。OS のすべてではなく、頼みを受けて機械を仲立ちする核。アプリは機械に直接さわらず、頼みはカーネルが受ける",
    "アプリは機械に直接さわらず、頼みはカーネルが受ける。",
  ),
  "key-pair": glossaryVisualWithDark(
    "key-pair",
    "scatter",
    "キーペアは、錠前と鍵の組。EC2 の Linux に SSH で入る例。秘密鍵は手元から出さない。鍵を持つ人だけが入れる。鍵は誰にも渡さない",
    "鍵を持つ人だけが入れる。鍵は誰にも渡さない。",
  ),
  "lambda": glossaryVisualWithDark(
    "lambda",
    "high-level",
    "lambda は、名前のない小さな関数。def で書いた関数と、働きは同じ。名札なしで、式 1 つをその場で渡す。長い処理は def で書く",
    "名札なしで、式 1 つをその場で渡す。長い処理は def で書く。",
  ),
  "latency": glossaryVisualWithDark(
    "latency",
    "bar",
    "レイテンシは、1 件が届くまでの待ち時間。1 秒に運べる量 (スループット) とは別の物。平均が小さくても、遅い 1 件が混じる。p99 や最大値でも見る",
    "平均が小さくても、遅い 1 件が混じる。p99 や最大値でも見る。",
  ),
  "layer": glossaryVisualWithDark(
    "layer",
    "medallion",
    "階層は、役割ごとに分けた層の積み重ね。上の層は、すぐ下の層だけを呼ぶのが基本。層ごとに役割が決まっているので、つなぎ方と返す値の意味を保てば入れ替えやすい",
    "層ごとに役割が決まっているので、つなぎ方と返す値の意味を保てば入れ替えやすい。",
  ),
  "least-privilege": glossaryVisualWithDark(
    "least-privilege",
    "high-level",
    "最小権限は、仕事に要る分の鍵だけ渡すこと。全部開く鍵は、落とした時の被害がいちばん大きい。要る分だけ渡すと、漏れた時の被害を狭く抑えやすい",
    "要る分だけ渡すと、漏れた時の被害を狭く抑えやすい。",
  ),
  "library": glossaryVisualWithDark(
    "library",
    "process",
    "ライブラリは、必要なときに取り出す道具箱。呼ぶのは自分で、道具は呼ばれるまで動かない。主役は自分のコード。ライブラリは、呼んだときだけ働く道具",
    "主役は自分のコード。ライブラリは、呼んだときだけ働く道具。",
  ),
  "linear-search": glossaryVisualWithDark(
    "linear-search",
    "data-flow",
    "線形探索は、端から 1 冊ずつ探す。並び順の決まりは要らない。端から順に見て、見つかった所で止める。無いと分かるのは最後まで見たとき",
    "端から順に見て、見つかった所で止める。無いと分かるのは最後まで見たとき。",
  ),
  "linked-list": glossaryVisualWithDark(
    "linked-list",
    "dp-integration",
    "連結リストは、次の場所を書いたカード。配列と違い、n 番目へは先頭から順にたどる。足すときは、矢印を付け替えるだけ。カードは動かさない",
    "足すときは、矢印を付け替えるだけ。カードは動かさない。",
  ),
  "linux": glossaryVisualWithDark(
    "linux",
    "high-level-parametric",
    "Linux は、車種ではなくエンジン (核)。Ubuntu や Android は、同じ系統の核に道具を載せた車種。Linux は OS の核 (カーネル)。Ubuntu や Android は、核に道具を組み合わせた物",
    "Linux は OS の核 (カーネル)。Ubuntu や Android は、核に道具を組み合わせた物。",
  ),
  "list-comprehension": glossaryVisualWithDark(
    "list-comprehension",
    "dp-security-matrix",
    "内包表記は、新しいリストを作る流れ作業。元のリストは、減らない。取り出す、選ぶ、加工する、並べる。for 文の短い書き方",
    "取り出す、選ぶ、加工する、並べる。for 文の短い書き方。",
  ),
  "list-ds": glossaryVisualWithDark(
    "list-ds",
    "it-current-state",
    "リストは、0 番から数える靴箱。番号は 1 ではなく 0 から始まる。f[1] は 2 つ目。番号は 0 から数え、最後の番号は長さより 1 小さい",
    "f[1] は 2 つ目。番号は 0 から数え、最後の番号は長さより 1 小さい。",
  ),
  "llamaindex": glossaryVisualWithDark(
    "llamaindex",
    "data-flow",
    "LlamaIndex は、質問に近い索引札を AI に渡す道具。AI モデルでもデータベースでもない。文書を答えさせる使い方の 1 つ。文書の中身を答えさせるとき、質問に近い札を選んで AI に渡す",
    "文書の中身を答えさせるとき、質問に近い札を選んで AI に渡す。",
  ),
  "loading": glossaryVisualWithDark(
    "loading",
    "sequence-oauth",
    "ローディングは、待っていると伝える砂時計。止まったのではなく、いま取りに行っている。待ち時間に表示を出すと、止まったと思われない。届いたら本物に変わる",
    "待ち時間に表示を出すと、止まったと思われない。届いたら本物に変わる。",
  ),
  "localstorage": glossaryVisualWithDark(
    "localstorage",
    "process",
    "localStorage は、ブラウザの中の引き出し。サーバーへは自動で送られず、閉じても残る。出どころ (オリジン) ごとに別。手元のブラウザに残り、別の出どころからは開けない",
    "手元のブラウザに残り、別の出どころからは開けない。",
  ),
  "lock": glossaryVisualWithDark(
    "lock",
    "architecture",
    "ロックは、更新中の行にかける札。既定の設定で主キーを指定して 1 行を更新すると、同じ行を書き換えたい人だけが待つ。主キー指定の更新なら、札がかかるのはその行だけ。ほかの行と、ふつうの SELECT は待たない",
    "主キー指定の更新なら、札がかかるのはその行だけ。ほかの行と、ふつうの SELECT は待たない。",
  ),
  "logical-operator": glossaryVisualWithDark(
    "logical-operator",
    "flowchart",
    "論理演算子は、条件を組み合わせて True か False にする。and は両方、or は少なくとも 1 つ。or は片方だけではなく、両方 ON でも点く",
    "or は片方だけではなく、両方 ON でも点く。",
  ),
  "loop": glossaryVisualWithDark(
    "loop",
    "loop",
    "ループは、周回数を数えながら走る。手順は 1 周分だけ書く。回すたびに数が増える。条件が外れたら止まる。数を増やし忘れると止まらない",
    "条件が外れたら止まる。数を増やし忘れると止まらない。",
  ),
  "max-width": glossaryVisualWithDark(
    "max-width",
    "sequence",
    "max-width は、広がりすぎないための上限。width: 100% と max-width: 600px を組み合わせたとき。固定の幅ではなく上限。狭い入れ物では縮み、広い入れ物では上限で止まる",
    "固定の幅ではなく上限。狭い入れ物では縮み、広い入れ物では上限で止まる。",
  ),
  "media-query": glossaryVisualWithDark(
    "media-query",
    "state",
    "メディアクエリは、表示領域の幅しだいで CSS を切り替える。ページは 1 つのまま、幅や向きなどで並びが変わる。別のページを作らず、同じ HTML に幅の条件を足す",
    "別のページを作らず、同じ HTML に幅の条件を足す。",
  ),
  "memoization": glossaryVisualWithDark(
    "memoization",
    "er",
    "メモ化は、答えを書き留めておく工夫。同じ引数の計算は、2 回目からメモを読むだけ。答えが変わる関数 (現在時刻など) には使えない",
    "答えが変わる関数 (現在時刻など) には使えない。",
  ),
  "memory": glossaryVisualWithDark(
    "memory",
    "layers",
    "メモリは、作業中だけ広げる机。保存する場所は、引き出し (SSD) のほう。電源を切ると机は空になる。残したい物は引き出しへ",
    "電源を切ると机は空になる。残したい物は引き出しへ。",
  ),
  "merge": glossaryVisualWithDark(
    "merge",
    "timeline",
    "マージは、別々に進んだ履歴を合わせること。元の貨車は消えず、上書きもされない。合流の貨車 M には親が 2 つ。C も D もそのまま残る",
    "合流の貨車 M には親が 2 つ。C も D もそのまま残る。",
  ),
  "merge-2": glossaryVisualWithDark(
    "merge-2",
    "swimlane",
    "マージは、2 人の直しを 1 つに合わせる。片方が上書きされて消えるわけではない。離れた行の直しは両方残る。合わせられない所は人が決め、合わさった所も確認する",
    "離れた行の直しは両方残る。合わせられない所は人が決め、合わさった所も確認する。",
  ),
  "merge-sort": glossaryVisualWithDark(
    "merge-sort",
    "flowchart",
    "マージソートは、割って混ぜる並べ替え。小さく割り、並んだ山どうしを混ぜる。整列済みの山は、先頭だけ比べれば混ぜられる",
    "整列済みの山は、先頭だけ比べれば混ぜられる。",
  ),
  "metadata": glossaryVisualWithDark(
    "metadata",
    "quadrant",
    "メタデータは、本の背表紙に貼った札。中身を開かなくても、札だけで探せる。メタデータは、データを説明する札。題や日付が書いてある",
    "メタデータは、データを説明する札。題や日付が書いてある。",
  ),
  "method": glossaryVisualWithDark(
    "method",
    "nested",
    "メソッドは、物に付いた操作ボタン。同じ名前でも、どの物のボタンかで結果が変わる。a.click() の a が、click の中で self になる",
    "a.click() の a が、click の中で self になる。",
  ),
  "method-definition": glossaryVisualWithDark(
    "method-definition",
    "tree",
    "メソッド定義は、レシピ札を書くこと。書いただけでは動かない。呼んだときに動く。def は札を書くだけ。obj.greet() と呼ぶと、初めて動く",
    "def は札を書くだけ。obj.greet() と呼ぶと、初めて動く。",
  ),
  "micro-interaction": glossaryVisualWithDark(
    "micro-interaction",
    "org-chart",
    "マイクロインタラクションは、押した結果を返す小さな動き。押せたかどうかが、その場で分かる。飾りではなく、押した結果をその場で返す合図",
    "飾りではなく、押した結果をその場で返す合図。",
  ),
  "middleware": glossaryVisualWithDark(
    "middleware",
    "layers",
    "ミドルウェアは、依頼が処理に届く前に通る関所。ログや認証を、関所として順に並べる (Express の例)。関所は next() で次へ渡し、渡さなければ先へ進まない",
    "関所は next() で次へ渡し、渡さなければ先へ進まない。",
  ),
  "missing-value": glossaryVisualWithDark(
    "missing-value",
    "venn",
    "欠損値は、測れていない空欄で、0 とは別物。0 で埋めると、平均がゆがむ。空欄を 0 で埋めると平均が下がる。空欄は空欄のまま扱う",
    "空欄を 0 で埋めると平均が下がる。空欄は空欄のまま扱う。",
  ),
  "mobile-first": glossaryVisualWithDark(
    "mobile-first",
    "pyramid",
    "モバイルファーストは、狭い画面から先に作る。スマホ用の別サイトは作らず、広くなったら足す。同じカードが、スマホでは 1 列、広い画面では 3 列に並ぶ",
    "同じカードが、スマホでは 1 列、広い画面では 3 列に並ぶ。",
  ),
  "module": glossaryVisualWithDark(
    "module",
    "radar",
    "モジュールは、部品棚の引き出し 1 つ。使うときは、引き出しの名前を付けて呼ぶ。1 つのファイルが 1 つの引き出し。import は開けるだけで、呼ぶときは名前を付ける",
    "1 つのファイルが 1 つの引き出し。import は開けるだけで、呼ぶときは名前を付ける。",
  ),
  "modulo-operator": glossaryVisualWithDark(
    "modulo-operator",
    "loop",
    "% は、割った余りを返す。パーセントではない。時計の針が戻る数。15 % 12 は 3 になる。余りが 0 なら、割り切れている",
    "15 % 12 は 3 になる。余りが 0 なら、割り切れている。",
  ),
  "mojibake": glossaryVisualWithDark(
    "mojibake",
    "data-lake",
    "文字化けは、読む表の取り違え。多くはファイルの数が無事で、読む表が違うだけ。数が残っていれば、表を合わせて読み直すと元に戻る",
    "数が残っていれば、表を合わせて読み直すと元に戻る。",
  ),
  "multi-stage": glossaryVisualWithDark(
    "multi-stage",
    "bar",
    "マルチステージは、作業台から完成品だけ運ぶ出荷の箱。道具は、最後の箱に入らない。最後の箱には、完成品だけが入る。だから小さく軽い",
    "最後の箱には、完成品だけが入る。だから小さく軽い。",
  ),
  "multiplicity": glossaryVisualWithDark(
    "multiplicity",
    "er",
    "多重度は、線の端の数で相手の個数を表す。注文伝票と明細カードを、両側から読む。端の数字は、反対側の 1 件から見た、この側の個数",
    "端の数字は、反対側の 1 件から見た、この側の個数。",
  ),
  "mutable": glossaryVisualWithDark(
    "mutable",
    "line",
    "ミュータブルは、作ったあとに中身を書き換えられること。list は同じ物の上で書き換わる。str や tuple は書き換えられない。書き換えると、同じ物を指す名前の全部に見える",
    "書き換えると、同じ物を指す名前の全部に見える。",
  ),
  "mysql": glossaryVisualWithDark(
    "mysql",
    "gantt",
    "MySQL は、SQL の伝票を受け付ける台帳室。SQL は共通の言葉。MySQL はそれを受ける製品の 1 つ。SQL は言葉、MySQL は台帳室。別の製品も SQL を使う",
    "SQL は言葉、MySQL は台帳室。別の製品も SQL を使う。",
  ),
  "mysql2-promise": glossaryVisualWithDark(
    "mysql2-promise",
    "scatter",
    "mysql2/promise は、結果を引換券で返す窓口。Node.js から MySQL に頼む道具。await で完了を待って受け取る。await で待ち、成功すると結果を受け取れる",
    "await で待ち、成功すると結果を受け取れる。",
  ),
  "neural-network": glossaryVisualWithDark(
    "neural-network",
    "layers",
    "ニューラルネットワークは、つまみ付きの足し算。脳の再現ではない。ずれたら、つまみを直す。掛けて足して曲げる部屋を層に重ね、ずれたらつまみを直す",
    "掛けて足して曲げる部屋を層に重ね、ずれたらつまみを直す。",
  ),
  "neuron": glossaryVisualWithDark(
    "neuron",
    "high-level",
    "ニューロンは、重みで水を足す桶。脳細胞のコピーではなく、重みを掛けて足し、活性化関数で出す小さな式。入力に重みを掛けて足し、活性化関数で出力を決める (桶はしきい値型の例)",
    "入力に重みを掛けて足し、活性化関数で出力を決める (桶はしきい値型の例)",
  ),
  "nodejs": glossaryVisualWithDark(
    "nodejs",
    "loop",
    "Node.js は、ブラウザの外で動かす場所。新しい言語ではなく、JavaScript の動く場所の 1 つ。同じ JavaScript でも、ブラウザは画面、Node.js はサーバーやファイルを触る",
    "同じ JavaScript でも、ブラウザは画面、Node.js はサーバーやファイルを触る。",
  ),
  "non-functional-requirement": glossaryVisualWithDark(
    "non-functional-requirement",
    "medallion",
    "非機能要件は、できること以外の「どれだけ」の決まり。速さ、止まらなさ、守りといった質の約束。同じ機能でも、質が足りないと使われなくなる",
    "同じ機能でも、質が足りないと使われなくなる。",
  ),
  "none": glossaryVisualWithDark(
    "none",
    "high-level",
    "None は、「書いていない」という値。0 や空の文字とは、別の値。None かどうかは is None で調べる。0 点を未入力にしない",
    "None かどうかは is None で調べる。0 点を未入力にしない。",
  ),
  "np-linspace": glossaryVisualWithDark(
    "np-linspace",
    "process",
    "np.linspace は、両端を含めて等間隔に点を打つ。3 つ目の数は刻み幅ではなく、点の個数。個数を決めると刻み幅は計算で決まる。刻み幅を先に決めるのは arange",
    "個数を決めると刻み幅は計算で決まる。刻み幅を先に決めるのは arange。",
  ),
  "oauth": glossaryVisualWithDark(
    "oauth",
    "sequence-oauth",
    "OAuth は、マスターキーではなく限定キーを渡すこと。パスワードは渡さず、範囲を決めた許可だけ渡す。OAuth は許可を貸す仕組み。ログインそのものではない",
    "OAuth は許可を貸す仕組み。ログインそのものではない。",
  ),
  "object": glossaryVisualWithDark(
    "object",
    "data-flow",
    "オブジェクトは、欄に名前のある名刺。番号ではなく、欄の名前で取り出す。名前と値の組を持つ。欄は後から足せる",
    "名前と値の組を持つ。欄は後から足せる。",
  ),
  "object-key": glossaryVisualWithDark(
    "object-key",
    "dp-integration",
    "オブジェクトキーは、ロッカーの名札。取り出す手がかりは、順番ではなく名前。キーは名前の札。user[0] は「0」という名前の札を探すので、この例では何も無い",
    "キーは名前の札。user[0] は「0」という名前の札を探すので、この例では何も無い。",
  ),
  "observability": glossaryVisualWithDark(
    "observability",
    "architecture",
    "オブザーバビリティは、外に出る信号から中を知ること。メーター、記録、道筋を手がかりに、中で何が起きたかを調べる。ログを出すだけでなく、信号を手がかりに原因を追える状態",
    "ログを出すだけでなく、信号を手がかりに原因を追える状態。",
  ),
  "one-way-data-flow": glossaryVisualWithDark(
    "one-way-data-flow",
    "high-level-parametric",
    "単方向データフローは、データを親から子へだけ流す原則。子が変えたいときは、値ではなく頼みを親へ送る。値は上から下へ。子が変えたいときは、頼みを上へ送る",
    "値は上から下へ。子が変えたいときは、頼みを上へ送る。",
  ),
  "operations-maintenance": glossaryVisualWithDark(
    "operations-maintenance",
    "dp-security-matrix",
    "運用・保守は、公開したあとの毎日と修理。動かし続けるのが運用、直して変えるのが保守。作って終わりではない。公開した日から、運用と保守が始まる",
    "作って終わりではない。公開した日から、運用と保守が始まる。",
  ),
  "operator": glossaryVisualWithDark(
    "operator",
    "it-current-state",
    "演算子は、値から結果を作る記号。+ は値の型で働きが変わり、== は等しいかを調べる。同じ + でも、数なら足し算、文字ならつなぐ。== は、等しいかを調べる",
    "同じ + でも、数なら足し算、文字ならつなぐ。== は、等しいかを調べる。",
  ),
  "output-device": glossaryVisualWithDark(
    "output-device",
    "sequence-oauth",
    "出力装置は、結果を人が受け取れる形に変える装置。光、音、紙にして外へ出す。入力は人から本体へ、出力は本体から人へ。向きで見分ける",
    "入力は人から本体へ、出力は本体から人へ。向きで見分ける。",
  ),
  "overfitting": glossaryVisualWithDark(
    "overfitting",
    "line",
    "過学習は、過去問に合わせすぎた状態。決定木の深さを変えた例 (数字は例)。訓練が高くても未知で下がる。訓練の点数だけでなく、未知データの点数が保てる深さを選ぶ",
    "訓練の点数だけでなく、未知データの点数が保てる深さを選ぶ。",
  ),
  "overload": glossaryVisualWithDark(
    "overload",
    "architecture",
    "オーバーロードは、形で呼び分ける投入口。同じ名前が複数あって使えるのは、Java などの言語。Python のように使えない言語では、同じ名前を書くと後の 1 つだけが残る",
    "Python のように使えない言語では、同じ名前を書くと後の 1 つだけが残る。",
  ),
  "override": glossaryVisualWithDark(
    "override",
    "flowchart",
    "オーバーライドは、支店だけが書き換えたレシピ。子が同じ名前で書き直しても、親は元のまま。子の curry だけが辛口になる。親の curry は甘口のまま残る",
    "子の curry だけが辛口になる。親の curry は甘口のまま残る。",
  ),
  "package-json": glossaryVisualWithDark(
    "package-json",
    "sequence",
    "package.json は、部品の注文書。書くだけでは届かない。届けるのは npm install。共有するのは注文書と package-lock.json。箱は各自の手元で作る",
    "共有するのは注文書と package-lock.json。箱は各自の手元で作る。",
  ),
  "package-lock-json": glossaryVisualWithDark(
    "package-lock-json",
    "state",
    "package-lock.json は、入れた版のレシート。package.json は『この範囲なら可』のメモ。lock は実際の版の記録。同じ版を、誰の手元でも再現できる",
    "同じ版を、誰の手元でも再現できる。",
  ),
  "pascal-case": glossaryVisualWithDark(
    "pascal-case",
    "er",
    "パスカルケースは、単語の頭を大文字にしてつなぐ書き方。先頭の 1 字も大文字にする。先頭が小文字なら別の書き方。クラスの名前によく使う。先頭が小文字のものは camelCase",
    "クラスの名前によく使う。先頭が小文字のものは camelCase。",
  ),
  "password": glossaryVisualWithDark(
    "password",
    "flowchart",
    "パスワードは、混ぜるより長くして使い回さない。混ぜる規則より、長さと使い回さないことが効く。1 つ漏れても、ほかのドアは開かない形にする",
    "1 つ漏れても、ほかのドアは開かない形にする。",
  ),
  "pay-as-you-go": glossaryVisualWithDark(
    "pay-as-you-go",
    "timeline",
    "従量課金は、使った量に単価を掛けて払う料金。使わない月は安く、使うほど高くなる。止め忘れも、動かした時間ぶんとして課金される",
    "止め忘れも、動かした時間ぶんとして課金される。",
  ),
  "permission": glossaryVisualWithDark(
    "permission",
    "swimlane",
    "パーミッションは、読む・書く・実行するの別々の鍵。ファイルごとに、3 組の人へ、種類別に許可を決める。rw-r--r-- は 644。許可は種類ごと、人の組ごとに別",
    "rw-r--r-- は 644。許可は種類ごと、人の組ごとに別。",
  ),
  "pipe": glossaryVisualWithDark(
    "pipe",
    "quadrant",
    "パイプは、左の出力を右の入力へ流す管。ls | wc -l は、ls が出した行の数を数える。| は管。左の標準出力が、そのまま右の標準入力になる。間にファイルは作らない",
    "| は管。左の標準出力が、そのまま右の標準入力になる。間にファイルは作らない。",
  ),
  "placeholder": glossaryVisualWithDark(
    "placeholder",
    "nested",
    "プレースホルダは、SQL の穴に値を後から渡す。値は文字のまま渡るので、SQL の形は変わらない。渡した値は SQL にならない。ただの名前として探す",
    "渡した値は SQL にならない。ただの名前として探す。",
  ),
  "platform-independent": glossaryVisualWithDark(
    "platform-independent",
    "tree",
    "プラットフォーム非依存は、同じ家電を国ごとの変換プラグで使うこと。Java の例。共通のバイトコードを、環境ごとの JVM が動かす。家電は同じで、つなぐプラグ (JVM) が環境ごとに違う",
    "家電は同じで、つなぐプラグ (JVM) が環境ごとに違う。",
  ),
  "plt-bar": glossaryVisualWithDark(
    "plt-bar",
    "org-chart",
    "plt.bar は、位置と高さを受けて棒を描く。数える処理は、自分で先に済ませる。渡すのは数え終わった高さで、生のデータではない",
    "渡すのは数え終わった高さで、生のデータではない。",
  ),
  "plt-figure": glossaryVisualWithDark(
    "plt-figure",
    "layers",
    "plt.figure は、グラフを描く新しい紙を用意する。plot は、その紙の上に線を描く。同じ紙に描けば重なり、紙を替えれば別のグラフになる",
    "同じ紙に描けば重なり、紙を替えれば別のグラフになる。",
  ),
  "plt-plot": glossaryVisualWithDark(
    "plt-plot",
    "venn",
    "plt.plot は、渡した順に点をつなぐ。なめらかにも、並べ替えもしない。線の形は、点の数だけでなく並べた順で決まる",
    "線の形は、点の数だけでなく並べた順で決まる。",
  ),
  "plt-scatter": glossaryVisualWithDark(
    "plt-scatter",
    "pyramid",
    "plt.scatter は、点を 1 つずつ置く。線で結ばず、順番にも意味は無い。点は 1 つずつ別。大きさも点ごとに変えられる",
    "点は 1 つずつ別。大きさも点ごとに変えられる。",
  ),
  "policy": glossaryVisualWithDark(
    "policy",
    "radar",
    "ポリシーは、誰が何をしてよいかを書いた規則書。何も書かなければ拒否で、Deny があれば Allow に勝つ。許可は書いて初めて通る。拒否が 1 つでもあれば止まる",
    "許可は書いて初めて通る。拒否が 1 つでもあれば止まる。",
  ),
  "polymorphism": glossaryVisualWithDark(
    "polymorphism",
    "loop",
    "ポリモーフィズムは、同じ合図で型ごとに違う動き。呼ぶ側の書き方は 1 つのまま。呼ぶ書き方は同じ 1 行。動きは、相手の型が決める",
    "呼ぶ書き方は同じ 1 行。動きは、相手の型が決める。",
  ),
  "pom-xml": glossaryVisualWithDark(
    "pom-xml",
    "nested",
    "pom.xml は、部品を取り寄せる注文書。住所 (3 つ組) を書くと、Maven が倉庫から取り寄せる。部品は自分で集めず、注文書に書く",
    "部品は自分で集めず、注文書に書く。",
  ),
  "pooling-layer": glossaryVisualWithDark(
    "pooling-layer",
    "data-lake",
    "プーリング層は、窓ごとに大きい値を残す層。学習で覚える値は無く、拾うだけで 4 分の 1 に縮む。最大値だけ残すので、少しずれても同じ答えになる",
    "最大値だけ残すので、少しずれても同じ答えになる。",
  ),
  "port-80": glossaryVisualWithDark(
    "port-80",
    "bar",
    "80 番ポートは、http:// が行く既定の出入口。URL に番号を書かなくても、http なら 80 番の扉へ行く。番号を省くと、http は 80 番、https は 443 番へ向かう",
    "番号を省くと、http は 80 番、https は 443 番へ向かう。",
  ),
  "port-forwarding": glossaryVisualWithDark(
    "port-forwarding",
    "line",
    "ポートフォワーディングは、壁を通す伝声管。手元のポートに話すと、SSH の道で奥の DB に届く。DB を外に公開せず、手元から届かせる",
    "DB を外に公開せず、手元から届かせる。",
  ),
  "port-number": glossaryVisualWithDark(
    "port-number",
    "gantt",
    "ポート番号は、サーバーの中のホーム番号。住所 (IP) は同じでも、番号で行き先のサービスが分かれる。ポートは差込口ではなく、サービスを分ける番号",
    "ポートは差込口ではなく、サービスを分ける番号。",
  ),
  "position": glossaryVisualWithDark(
    "position",
    "scatter",
    "position は、貼る基準を決める指定。absolute は画面ではなく、位置を決めた親が基準。absolute の基準は、位置を決めた一番近い親",
    "absolute の基準は、位置を決めた一番近い親。",
  ),
  "post-redirect-get": glossaryVisualWithDark(
    "post-redirect-get",
    "medallion",
    "注文のあとは、別の窓口へ案内する。結果の画面で更新しても、注文は 1 回のまま。更新でやり直されるのは、見るだけの GET になる",
    "更新でやり直されるのは、見るだけの GET になる。",
  ),
  "postgresql": glossaryVisualWithDark(
    "postgresql",
    "high-level",
    "PostgreSQL は、同じ SQL でも方言が違う窓口。MySQL と取り替えるときは、書き方を確かめる。SQL は共通でも、自動の番号と結果の返し方は書き方が違う",
    "SQL は共通でも、自動の番号と結果の返し方は書き方が違う。",
  ),
  "presentational-component": glossaryVisualWithDark(
    "presentational-component",
    "process",
    "プレゼンテーショナルは、渡された値を見た目にする部品。データを取りに行くのは、持ち主である親。同じ部品に別の値を渡せば別の表示になる。取りに行くのは親の仕事",
    "同じ部品に別の値を渡せば別の表示になる。取りに行くのは親の仕事。",
  ),
  "prime-number": glossaryVisualWithDark(
    "prime-number",
    "data-flow",
    "素数は、1 と自分自身でしか割り切れない 2 以上の整数。積み木を長方形に並べて確かめる。2 以上で、1 列にしか並ばない数が素数。9 は奇数でも違う",
    "2 以上で、1 列にしか並ばない数が素数。9 は奇数でも違う。",
  ),
  "primitive": glossaryVisualWithDark(
    "primitive",
    "dp-integration",
    "プリミティブは、書き換えられない 1 つの値。文字列も、1 文字だけ直すことはできない。変えたいときは、新しい値を作って名札を付け替える",
    "変えたいときは、新しい値を作って名札を付け替える。",
  ),
  "primitive-type": glossaryVisualWithDark(
    "primitive-type",
    "high-level-parametric",
    "プリミティブ型は、言語が用意した基本の型。Java は 8 つだけ。String はその外にある。int や boolean などの 8 つが基本の型。String は別の扱い",
    "int や boolean などの 8 つが基本の型。String は別の扱い。",
  ),
  "private-key": glossaryVisualWithDark(
    "private-key",
    "dp-security-matrix",
    "秘密鍵は自分だけの鍵。配ってよいのは錠。公開鍵は錠、秘密鍵は鍵。取り違えて鍵を渡さない。渡してよいのは錠 (.pub) だけ",
    "渡してよいのは錠 (.pub) だけ。",
  ),
  "process": glossaryVisualWithDark(
    "process",
    "it-current-state",
    "プロセスは、楽譜ではなく今鳴っている演奏。プログラムは置いてある物。動いている 1 回分がプロセス。同じプログラムから、番号の違うプロセスをいくつも動かせる",
    "同じプログラムから、番号の違うプロセスをいくつも動かせる。",
  ),
  "process-flow": glossaryVisualWithDark(
    "process-flow",
    "sequence-oauth",
    "処理は、自動販売機の中の手順。入力を、決まった手順で出力に変える。処理は魔法ではなく、決まった手順の積み重ね。順番、分岐、繰り返しで進む",
    "処理は魔法ではなく、決まった手順の積み重ね。順番、分岐、繰り返しで進む。",
  ),
  "promise": glossaryVisualWithDark(
    "promise",
    "state",
    "Promise は、料理が出来たら鳴るブザー。手に入るのは料理ではなく、引換えのブザー。結果が決まると、成功か失敗のどちらかで知らされる",
    "結果が決まると、成功か失敗のどちらかで知らされる。",
  ),
  "promise-all": glossaryVisualWithDark(
    "promise-all",
    "architecture",
    "Promise.all は、渡した Promise が全部そろうのを待つ。開始済みの処理をまとめて待つ。1 つでも失敗すると、全体の結果が失敗になる。全部成功なら、待ち時間は一番長い 1 つぶん。結果は渡した順に並ぶ",
    "全部成功なら、待ち時間は一番長い 1 つぶん。結果は渡した順に並ぶ。",
  ),
  "prompt-engineering": glossaryVisualWithDark(
    "prompt-engineering",
    "loop",
    "プロンプトエンジニアリングは、依頼の書き方を直す作業。呪文の裏技ではなく、欄を足して、試して、直す。魔法の言葉は要らない。条件を足し、結果を見て直す",
    "魔法の言葉は要らない。条件を足し、結果を見て直す。",
  ),
  "prompttemplate": glossaryVisualWithDark(
    "prompttemplate",
    "high-level-parametric",
    "PromptTemplate は、穴あきの書式。文字列のコピーではなく、毎回ちがう値を差し込む。書式は 1 つで、値を替えて何通りでも組み立てる",
    "書式は 1 つで、値を替えて何通りでも組み立てる。",
  ),
  "property": glossaryVisualWithDark(
    "property",
    "flowchart",
    "プロパティは、窓口を通して読み書きする値。外から見ると変数のようで、裏では窓口が動く。set の窓口で値を検査できる。中の金庫へは直接さわれない",
    "set の窓口で値を検査できる。中の金庫へは直接さわれない。",
  ),
  "protocol": glossaryVisualWithDark(
    "protocol",
    "sequence",
    "プロトコルは、通信の作法。話す順番と書式をそろえて、初めて通じる。ソフトの名前ではなく、やり取りの決まりごと",
    "ソフトの名前ではなく、やり取りの決まりごと。",
  ),
  "pseudo-class": glossaryVisualWithDark(
    "pseudo-class",
    "state",
    "擬似クラスは、状態で要素を選ぶ条件。HTML に class を書かなくても、状態で当たる。マウスが乗った間だけ button:hover の条件に合い、スタイルが効く",
    "マウスが乗った間だけ button:hover の条件に合い、スタイルが効く。",
  ),
  "public-key": glossaryVisualWithDark(
    "public-key",
    "er",
    "公開鍵は、誰に配ってもよい錠前。開けられるのは、秘密鍵を持つ本人だけ。かける錠前は配る。開ける秘密鍵は渡さない",
    "かける錠前は配る。開ける秘密鍵は渡さない。",
  ),
  "pull-request": glossaryVisualWithDark(
    "pull-request",
    "timeline",
    "プルリクエストは、本線に取り込んでほしい依頼。出しただけでは本線は変わらず、確認が済んでから入る。依頼を出して確認を受け、承認されて初めて本線へ入る",
    "依頼を出して確認を受け、承認されて初めて本線へ入る。",
  ),
  "push": glossaryVisualWithDark(
    "push",
    "swimlane",
    "push は、足りない分だけを書庫へ運ぶ。コミットしただけでは、書庫に届かない。手元に撮った写真は、push するまで相手には見えない",
    "手元に撮った写真は、push するまで相手には見えない。",
  ),
  "python": glossaryVisualWithDark(
    "python",
    "quadrant",
    "Python は、字下げでまとまりを決める言語。同じ行でも、字下げが 1 つ違うと動きが変わる。字下げが if の中の範囲を決める。ずらすと別のプログラムになる",
    "字下げが if の中の範囲を決める。ずらすと別のプログラムになる。",
  ),
  "query": glossaryVisualWithDark(
    "query",
    "swimlane",
    "クエリは、データベースへの依頼票。条件を書けば、ほしい行だけを選んで届けてもらえる。絞るのはデータベースの仕事。返ってくるのは、合う行だけ",
    "絞るのはデータベースの仕事。返ってくるのは、合う行だけ。",
  ),
  "query-parameter": glossaryVisualWithDark(
    "query-parameter",
    "nested",
    "クエリパラメータは、住所の後ろに付ける注文書き。? のあとの key=value が、サーバーに届く。? のあとは飾りではなく、URL の一部として届く注文書き",
    "? のあとは飾りではなく、URL の一部として届く注文書き。",
  ),
  "queue": glossaryVisualWithDark(
    "queue",
    "data-flow",
    "キューは、窓口に並ぶ行列。先に並んだ人から、先に済ませる。入るのは後ろ、出るのは前。FIFO (先入れ先出し)",
    "入るのは後ろ、出るのは前。FIFO (先入れ先出し)",
  ),
  "quick-sort": glossaryVisualWithDark(
    "quick-sort",
    "tree",
    "クイックソートは、基準を決めて左右に分ける。平均は速いが、基準の選び方が悪いと遅くなる。基準が真ん中の値に近いほど、左右が半々になる",
    "基準が真ん中の値に近いほど、左右が半々になる。",
  ),
  "raci": glossaryVisualWithDark(
    "raci",
    "org-chart",
    "RACI は、1 つの仕事に 4 つの役を割り振る決まり。手を動かす人は何人でもよいが、最終責任の人は 1 人だけ。R は動く人、A は最終責任の 1 人、C は相談を受ける人、I は報告を受ける人",
    "R は動く人、A は最終責任の 1 人、C は相談を受ける人、I は報告を受ける人。",
  ),
  "re-render": glossaryVisualWithDark(
    "re-render",
    "flowchart",
    "React の再描画は、下書きを作り直して見比べる。画面を全部描き直すわけではない。関数をもう一度走らせて下書きを作り、違う所だけ DOM を更新する",
    "関数をもう一度走らせて下書きを作り、違う所だけ DOM を更新する。",
  ),
  "react": glossaryVisualWithDark(
    "react",
    "layers",
    "React は、画面を部品で組み立てる JavaScript のライブラリ。言語ではなく、JavaScript で書く道具。state が変わると、その部品の表示を計算し直す",
    "state が変わると、その部品の表示を計算し直す。",
  ),
  "react-reactnode": glossaryVisualWithDark(
    "react-reactnode",
    "venn",
    "React.ReactNode は、画面に描ける物すべてを受ける型。要素だけでなく、文字列、数、null も入る。要素だけの型 (ReactElement) より広い。ただしふつうのオブジェクトは入らない",
    "要素だけの型 (ReactElement) より広い。ただしふつうのオブジェクトは入らない。",
  ),
  "read-replica": glossaryVisualWithDark(
    "read-replica",
    "high-level-parametric",
    "リードレプリカは、読むためだけの写し。書くのは原本だけ。写しへの反映は遅れることがある。登録した直後の読みは、写しだとまだ古いことがある",
    "登録した直後の読みは、写しだとまだ古いことがある。",
  ),
  "rebase": glossaryVisualWithDark(
    "rebase",
    "timeline",
    "rebase は、貨車を付け替えて作り直すこと。merge と違い、履歴は一直線になる。付け替えた貨車は別物 (D は D')。公開済みの履歴は、合意なく変えない",
    "付け替えた貨車は別物 (D は D')。公開済みの履歴は、合意なく変えない。",
  ),
  "record-db": glossaryVisualWithDark(
    "record-db",
    "pyramid",
    "レコードは、表の 1 行にあたる 1 件分のまとまり。1 人分の情報が、カードなら 1 枚、表なら 1 行。行がレコード、列が項目。1 行が 1 件の記録",
    "行がレコード、列が項目。1 行が 1 件の記録。",
  ),
  "recursion": glossaryVisualWithDark(
    "recursion",
    "radar",
    "再帰は、人形の中の同じ人形。自分の中で、小さい自分を呼ぶ。止まる条件に着いたら、戻りながら答えを組み立てる",
    "止まる条件に着いたら、戻りながら答えを組み立てる。",
  ),
  "redirect": glossaryVisualWithDark(
    "redirect",
    "loop",
    "リダイレクトは、引っ越しの貼り紙を見て新住所へ行くこと。新しい住所へ、もう一度取りに行くのはブラウザ。サーバーが新しい住所を教え、ブラウザが自分で行き直す。アドレス欄も変わる",
    "サーバーが新しい住所を教え、ブラウザが自分で行き直す。アドレス欄も変わる。",
  ),
  "reference-type": glossaryVisualWithDark(
    "reference-type",
    "data-lake",
    "参照型は、同じ倉庫の合鍵。b = a は、中身ではなく鍵をもう 1 本作る。鍵が 2 本でも倉庫は 1 つ。どちらの鍵でも同じ中身を変えられる",
    "鍵が 2 本でも倉庫は 1 つ。どちらの鍵でも同じ中身を変えられる。",
  ),
  "region": glossaryVisualWithDark(
    "region",
    "bar",
    "リージョンは、拠点の地域を選ぶ設定。国の名前ではなく、データセンターが集まる地理的な場所。近さと決まりで選ぶ。リソースは選んだ地域に作られる",
    "近さと決まりで選ぶ。リソースは選んだ地域に作られる。",
  ),
  "regression": glossaryVisualWithDark(
    "regression",
    "line",
    "回帰は、点の並びに線を引いて数を読む。線形回帰の例。家賃のような連続した数を予測する。「どれか」を選ぶのは分類。「いくらか」を読むのが回帰",
    "「どれか」を選ぶのは分類。「いくらか」を読むのが回帰。",
  ),
  "rendering": glossaryVisualWithDark(
    "rendering",
    "line",
    "レンダリングは、文字の設計図から画面の絵を描くこと。画面に見えているのは、コードそのものではない。ブラウザが HTML と CSS を読み、並びと見た目を決めて、画面に描く",
    "ブラウザが HTML と CSS を読み、並びと見た目を決めて、画面に描く。",
  ),
  "repetition": glossaryVisualWithDark(
    "repetition",
    "gantt",
    "反復は、1 つの処理を何度もまわす仕組み。答案の山に、同じ判子を押していく。処理は 1 回だけ書く。まわる数は、対象か条件が決める",
    "処理は 1 回だけ書く。まわる数は、対象か条件が決める。",
  ),
  "repl": glossaryVisualWithDark(
    "repl",
    "gantt",
    "REPL は、1 行ずつ実行して試す場所。読む・評価する・表示するを、1 行ごとに繰り返す。ファイルは作らず、1 行ずつ試せる。閉じると変数は消える",
    "ファイルは作らず、1 行ずつ試せる。閉じると変数は消える。",
  ),
  "repository": glossaryVisualWithDark(
    "repository",
    "scatter",
    "リポジトリは、変更の履歴ごと入った書庫。ファイルを置くフォルダではなく、過去も全部しまってある。リポジトリには、今のファイルと、変更の履歴が入っている",
    "リポジトリには、今のファイルと、変更の履歴が入っている。",
  ),
  "req-params": glossaryVisualWithDark(
    "req-params",
    "medallion",
    "req.params は、URL の道の途中の値を取り出す。ルートの :id に当たった部分が、文字列で入る。? の後ろの値は、req.params ではなく req.query に入る",
    "? の後ろの値は、req.params ではなく req.query に入る。",
  ),
  "request-body": glossaryVisualWithDark(
    "request-body",
    "high-level",
    "リクエストボディは、リクエストの中身を入れる所。URL や宛名面ではなく、封筒の中の手紙にあたる。値は宛名に書かず中身に入れて、種類の荷札を付ける",
    "値は宛名に書かず中身に入れて、種類の荷札を付ける。",
  ),
  "request-header": glossaryVisualWithDark(
    "request-header",
    "sequence",
    "リクエストヘッダーは、封筒の表書き。表書きは送る人が書く。受け取る側は鵜呑みにしない。表書きの値は偽れる。サーバーは認証と権限を確かめ直す",
    "表書きの値は偽れる。サーバーは認証と権限を確かめ直す。",
  ),
  "request-scope": glossaryVisualWithDark(
    "request-scope",
    "process",
    "リクエストスコープは、1 回の依頼で使い捨てる箱。セッションと違い、次の依頼には持ち越せない。redirect は新しい依頼なので、前の箱の中身は無くなる",
    "redirect は新しい依頼なので、前の箱の中身は無くなる。",
  ),
  "requirements-txt": glossaryVisualWithDark(
    "requirements-txt",
    "data-flow",
    "requirements.txt は、pip に渡す買い物メモ。置いただけでは読まれない。pip install -r で渡す。ただの文字のメモ。-r で渡して初めて、書いた行が 1 つずつ入る",
    "ただの文字のメモ。-r で渡して初めて、書いた行が 1 つずつ入る。",
  ),
  "response-header": glossaryVisualWithDark(
    "response-header",
    "sequence",
    "レスポンスヘッダーは、返事の封筒に貼るシール。本文とは別に、外から読める付帯情報。ブラウザは、手紙を開く前に外のシールを読んで扱いを決める",
    "ブラウザは、手紙を開く前に外のシールを読んで扱いを決める。",
  ),
  "response-ok": glossaryVisualWithDark(
    "response-ok",
    "dp-integration",
    "response.ok は、返事が 200 番台かを示すランプ。404 や 500 でも返事が届けば fetch は成功で、ok が false になるだけ。HTTP のエラーは例外にならない。自分で ok を見て分ける",
    "HTTP のエラーは例外にならない。自分で ok を見て分ける。",
  ),
  "responsibility-segregation": glossaryVisualWithDark(
    "responsibility-segregation",
    "high-level-parametric",
    "責務分割は、食堂の係を分けること。同じ理由で変わる仕事を 1 つにまとめる。役目ごとに分けると、変更の影響を狭めやすい。直す所が少なくて済む",
    "役目ごとに分けると、変更の影響を狭めやすい。直す所が少なくて済む。",
  ),
  "responsive-design": glossaryVisualWithDark(
    "responsive-design",
    "high-level",
    "レスポンシブは、幅に合わせて並べ替える。ページは 1 つ。スマホ用の別ページは作らない。同じ HTML と CSS のまま、画面の幅で段の数が変わる",
    "同じ HTML と CSS のまま、画面の幅で段の数が変わる。",
  ),
  "rest-api": glossaryVisualWithDark(
    "rest-api",
    "state",
    "REST API は、引き出しの住所と動作の札。REST は設計の考え方。URL で物を、メソッドで動作を示す。REST API でよく使う基本は、URL で物を、メソッドで動作を示す",
    "REST API でよく使う基本は、URL で物を、メソッドで動作を示す。",
  ),
  "retrieval": glossaryVisualWithDark(
    "retrieval",
    "flowchart",
    "検索 (Retrieval) は、本棚から数冊だけ取り出すこと。言葉の一致ではなく、意味の近さで選ぶ方式もある。取り出した数冊だけが AI に渡る。全部を読むのではない",
    "取り出した数冊だけが AI に渡る。全部を読むのではない。",
  ),
  "return-value": glossaryVisualWithDark(
    "return-value",
    "dp-security-matrix",
    "戻り値は、取り出し口に出てくる商品。print は表示窓に出すだけ。受け取れるのは return。return した値を、呼んだ側が変数で受け取る",
    "return した値を、呼んだ側が変数で受け取る。",
  ),
  "review": glossaryVisualWithDark(
    "review",
    "it-current-state",
    "レビューは、混ぜる前に別の人が付ける付箋。間違い探しではなく、よくするための気づき。直して承認をもらってから、main に入れる。入れる前に気づける",
    "直して承認をもらってから、main に入れる。入れる前に気づける。",
  ),
  "rnn-transformer": glossaryVisualWithDark(
    "rnn-transformer",
    "quadrant",
    "Transformer は、全員を一度に見る円卓。RNN は伝言ゲーム。前から順にしか渡せない。全部の語を見比べ、語順の印も持ち、計算を並列にして速く学べる",
    "全部の語を見比べ、語順の印も持ち、計算を並列にして速く学べる。",
  ),
  "rollback": glossaryVisualWithDark(
    "rollback",
    "sequence-oauth",
    "ロールバックは、動く版を前の正常な版へ戻すこと。公開方式の 1 例。旗を前の版へ付け替える。戻すのは公開中の旗。この例では v3 も棚に残るので、直して出し直せる",
    "戻すのは公開中の旗。この例では v3 も棚に残るので、直して出し直せる。",
  ),
  "root-user": glossaryVisualWithDark(
    "root-user",
    "architecture",
    "ルートユーザーは、金庫にしまうマスターキー。AWS のアカウントを作った時の全権。日常には使わない。ふだんの作業は、権限を絞った別の入口 (ユーザーやロール) でする",
    "ふだんの作業は、権限を絞った別の入口 (ユーザーやロール) でする。",
  ),
  "router": glossaryVisualWithDark(
    "router",
    "flowchart",
    "ルーターは、URL を見て出す画面を選ぶ分岐器。URL ごとに、画面の部品を差し替える。URL と画面の対応表で、出す部品が決まる。切り替わるのは、その部品だけ",
    "URL と画面の対応表で、出す部品が決まる。切り替わるのは、その部品だけ。",
  ),
  "routing": glossaryVisualWithDark(
    "routing",
    "sequence",
    "ルーティングは、手紙を振り分ける区分棚。Web アプリでは、URL とメソッドを見て担当へ回す。URL がファイルの場所とは限らない。札に合う担当へ回る",
    "URL がファイルの場所とは限らない。札に合う担当へ回る。",
  ),
  "salt": glossaryVisualWithDark(
    "salt",
    "state",
    "ソルトは、同じ値にしないための塩。秘密にする鍵ではなく、人ごとに変える味付け。塩は DB にそのまま並べてよい。目的は同じ値を作らせないこと",
    "塩は DB にそのまま並べてよい。目的は同じ値を作らせないこと。",
  ),
  "sandbox": glossaryVisualWithDark(
    "sandbox",
    "nested",
    "Sandbox は、外へ届きにくくする砂場。AI が書いたコードは、囲いの中で動かす。壊しても外へ届きにくい。出入口は許可制にする",
    "壊しても外へ届きにくい。出入口は許可制にする。",
  ),
  "schema": glossaryVisualWithDark(
    "schema",
    "er",
    "スキーマは、表の置き方を決めた設計図。中身ではなく、どんな表と列をどう結ぶかの決まり。設計図は同じでも、中身は家ごとに別。設計図と中身は別の物",
    "設計図は同じでも、中身は家ごとに別。設計図と中身は別の物。",
  ),
  "scikit-learn": glossaryVisualWithDark(
    "scikit-learn",
    "swimlane",
    "scikit-learn は、持ち手がそろった工具箱。モデルそのものではなく、Python の機械学習の道具箱。多くの道具が同じ持ち手。本格的な深層学習は別の箱を使う",
    "多くの道具が同じ持ち手。本格的な深層学習は別の箱を使う。",
  ),
  "scope": glossaryVisualWithDark(
    "scope",
    "timeline",
    "スコープは、部屋の中だけのホワイトボード。部屋の中で書いた変数は、外から見えない。中から外は読める。外から中は読めない",
    "中から外は読める。外から中は読めない。",
  ),
  "security-group": glossaryVisualWithDark(
    "security-group",
    "swimlane",
    "セキュリティグループは、玄関の許可名簿。受信は、書いた相手とポートだけ通す。拒否は書かない。許可を足した分だけ通る。許可した通信の返事も通る",
    "許可を足した分だけ通る。許可した通信の返事も通る。",
  ),
  "seed-data": glossaryVisualWithDark(
    "seed-data",
    "loop",
    "シードデータは、DB に最初に撒く種。見本や初期値を撒くスクリプト。何度撒いても同じ状態になるよう書く。あれば更新、無ければ作る形で書くと、2 回撒いても 3 つのまま",
    "あれば更新、無ければ作る形で書くと、2 回撒いても 3 つのまま。",
  ),
  "selection-sort": glossaryVisualWithDark(
    "selection-sort",
    "quadrant",
    "選択ソートは、残りから最小を選ぶ。隣どうしの入れ替えではない。残りから一番小さい物を選び、先頭と入れ替える。先頭から 1 本ずつ確定する",
    "残りから一番小さい物を選び、先頭と入れ替える。先頭から 1 本ずつ確定する。",
  ),
  "selector": glossaryVisualWithDark(
    "selector",
    "nested",
    "セレクタは、スタイルを届ける宛先の書き方。当たった要素の全部に、同じスタイルが届く。宛先に当たった全部に届く。1 つだけとは限らない",
    "宛先に当たった全部に届く。1 つだけとは限らない。",
  ),
  "self-attention": glossaryVisualWithDark(
    "self-attention",
    "dp-security-matrix",
    "Self-Attention は、語どうしを結ぶ糸の太さ。1 つの語だけを選ぶのではなく、文中の語に重みをつけて混ぜる。太い糸の語を多く、細い糸の語を少し混ぜて、文脈を反映した表現にする",
    "太い糸の語を多く、細い糸の語を少し混ぜて、文脈を反映した表現にする。",
  ),
  "semantic-html": glossaryVisualWithDark(
    "semantic-html",
    "it-current-state",
    "セマンティック HTML は、箱に付けるラベル。見た目が同じでも、機械が読む手がかりが違う。main などの札は、読み上げソフトが主要な領域へ移動する手がかりになる",
    "main などの札は、読み上げソフトが主要な領域へ移動する手がかりになる。",
  ),
  "semantic-tag": glossaryVisualWithDark(
    "semantic-tag",
    "tree",
    "セマンティックタグは、場所の役割を伝える名札。見た目は同じでも、読み上げや検索が構造をつかめる。div だけでは役割が伝わらない。名札があれば本文へ飛べる",
    "div だけでは役割が伝わらない。名札があれば本文へ飛べる。",
  ),
  "separation-of-concerns": glossaryVisualWithDark(
    "separation-of-concerns",
    "org-chart",
    "責務分離は、変わる理由ごとにコードを分ける考え方。細かく分けること自体が目的ではない。全部入りの 1 つにせず、変わる理由ごとの持ち場に分ける",
    "全部入りの 1 つにせず、変わる理由ごとの持ち場に分ける。",
  ),
  "sequence": glossaryVisualWithDark(
    "sequence",
    "layers",
    "シーケンスは、番号札を出す発行機。札は渡したら戻らない。取り消しても欠番になる。番号は重ならないための物で、隙間がないことは保証しない",
    "番号は重ならないための物で、隙間がないことは保証しない。",
  ),
  "sequence-diagram": glossaryVisualWithDark(
    "sequence-diagram",
    "venn",
    "シーケンス図は、時間を下へ流して往復を並べる図。手順を書くフローチャートと違い、登場者どうしのやり取りを見る。横は登場者、縦は時間。上から下へ読むと、やり取りの順が分かる",
    "横は登場者、縦は時間。上から下へ読むと、やり取りの順が分かる。",
  ),
  "server": glossaryVisualWithDark(
    "server",
    "org-chart",
    "サーバーは、呼ばれたら返す係。機械の種類ではなく、待ち受けて返す役割のこと。大きな機械である必要はない。待ち受けて返せば、サーバー",
    "大きな機械である必要はない。待ち受けて返せば、サーバー。",
  ),
  "servlet-container": glossaryVisualWithDark(
    "servlet-container",
    "pyramid",
    "サーブレットコンテナは、サーブレットを預かって呼ぶ入れ物。Tomcat などが、生成から終了まで面倒を見る。サーブレットは自分で動かない。管理人のコンテナが呼ぶ",
    "サーブレットは自分で動かない。管理人のコンテナが呼ぶ。",
  ),
  "session": glossaryVisualWithDark(
    "session",
    "data-flow",
    "セッションは、サーバーが預かる荷物。ブラウザは番号札を持つ。サーバーに状態を置く方式。札 (セッション ID) に中身は無い。札を無効にすれば、札を持っていても入れない",
    "札を無効にすれば、札を持っていても入れない。",
  ),
  "session-hijacking": glossaryVisualWithDark(
    "session-hijacking",
    "radar",
    "セッションハイジャックは、番号札を拾われること。札を見せた人が、パスワードなしで本人になる。札を盗ませない。盗まれても使い続けさせない",
    "札を盗ませない。盗まれても使い続けさせない。",
  ),
  "session-invalidate": glossaryVisualWithDark(
    "session-invalidate",
    "loop",
    "session.invalidate は、サーバーのロッカーごと捨てる。ブラウザの手元の札は残るが、開ける先がもう無い。セッション方式のログアウトは、サーバーの記録を捨てること",
    "セッション方式のログアウトは、サーバーの記録を捨てること。",
  ),
  "session-scope": glossaryVisualWithDark(
    "session-scope",
    "data-lake",
    "セッションスコープは、客ごとに別の買い物かご。値は利用者ごとに保持され、ほかの人には見えない。セッションスコープは、客ごとに別。使わない時間が続くと捨てる",
    "セッションスコープは、客ごとに別。使わない時間が続くと捨てる。",
  ),
  "session-setattribute": glossaryVisualWithDark(
    "session-setattribute",
    "sequence",
    "session.setAttribute は、サーバーのロッカーに入れる。Cookie で管理する場合、ブラウザが持つのは番号札だけで、中身は持たない。名前などの中身はサーバーの箱の中。ブラウザは札を見せて取り出す",
    "名前などの中身はサーバーの箱の中。ブラウザは札を見せて取り出す。",
  ),
  "session-timeout": glossaryVisualWithDark(
    "session-timeout",
    "bar",
    "ログインの期限を持つのは、サーバー。ブラウザを閉じても、サーバーの札は期限まで残る。閉じただけでは、ログアウトにならない",
    "閉じただけでは、ログアウトにならない。",
  ),
  "set-ds": glossaryVisualWithDark(
    "set-ds",
    "venn",
    "セットは、同じ値を 1 つにまとめる集まり。Python の set。スタンプラリーの台紙と同じ。同じ値は 1 つ。番号では取り出せない。重複を消すのに使う",
    "同じ値は 1 つ。番号では取り出せない。重複を消すのに使う。",
  ),
  "setautocommit": glossaryVisualWithDark(
    "setautocommit",
    "line",
    "setAutoCommit は、伝票をその場で出すか束ねるかの切り替え。JDBC で、この接続の自動確定を切り替える。false は自動確定をやめるだけ。確定は commit() で自分でする",
    "false は自動確定をやめるだけ。確定は commit() で自分でする。",
  ),
  "shared-responsibility-model": glossaryVisualWithDark(
    "shared-responsibility-model",
    "swimlane",
    "共同責任モデルは、建物と部屋で守る人が分かれる。クラウドに預けても、利用者が守る仕事は残る (AWS では責任共有モデル)。データの中身とアクセス権は、どの種類でも利用者の責任",
    "データの中身とアクセス権は、どの種類でも利用者の責任。",
  ),
  "shared-types": glossaryVisualWithDark(
    "shared-types",
    "gantt",
    "型共有は、API の形を 1 か所に書いて両側で読む。フロントとバックが、同じ型の定義を使う。食い違いは動かす前の検査で気づける。実行時の中身までは守らない",
    "食い違いは動かす前の検査で気づける。実行時の中身までは守らない。",
  ),
  "shebang": glossaryVisualWithDark(
    "shebang",
    "scatter",
    "シバンは、読む係を書いた 1 行目。ただのコメントではなく、実行のときに読まれる宛名。./ で実行するとき、カーネルが 1 行目の宛名へ渡す",
    "./ で実行するとき、カーネルが 1 行目の宛名へ渡す。",
  ),
  "shell": glossaryVisualWithDark(
    "shell",
    "medallion",
    "シェルは、* を書き換える受付係。ls などのコマンドは、書き換え後の引数を受け取る。* を a.md b.md に変えるのは、コマンドではなくシェル",
    "* を a.md b.md に変えるのは、コマンドではなくシェル。",
  ),
  "shell-script": glossaryVisualWithDark(
    "shell-script",
    "high-level",
    "シェルスクリプトは、コマンドの台本。別世界の言語ではなく、打つ行を書き並べたファイル。ふつうは上から順に、失敗しても次へ進む。set -e で止められる",
    "ふつうは上から順に、失敗しても次へ進む。set -e で止められる。",
  ),
  "side-effect": glossaryVisualWithDark(
    "side-effect",
    "process",
    "副作用は、戻り値のほかに外を変えること。悪者ではなく、保存や表示に必要。ただ見落としやすい。同じ操作でも、外が変わると 2 回目の結果が違う",
    "同じ操作でも、外が変わると 2 回目の結果が違う。",
  ),
  "sidebar": glossaryVisualWithDark(
    "sidebar",
    "data-flow",
    "サイドバーは、画面の脇に置く縦の欄。メニューなどを置く。幅が狭いと畳む。サイドバーは、脇に置く欄。共通のメニューはページを変えても残る",
    "サイドバーは、脇に置く欄。共通のメニューはページを変えても残る。",
  ),
  "sieve-of-eratosthenes": glossaryVisualWithDark(
    "sieve-of-eratosthenes",
    "dp-integration",
    "エラトステネスのふるいは、倍数を消して素数を残す。各数を 1 つずつ割って確かめなくてよい。素数の倍数を順に消して、消えずに残った数が素数",
    "素数の倍数を順に消して、消えずに残った数が素数。",
  ),
  "signed-url": glossaryVisualWithDark(
    "signed-url",
    "high-level-parametric",
    "署名付き URL は、日付入りの入場券。持っている人は誰でも、期限と権限が有効な間は開けられる。暗号化ではなく、期限と署名が付いているだけ",
    "暗号化ではなく、期限と署名が付いているだけ。",
  ),
  "single-thread": glossaryVisualWithDark(
    "single-thread",
    "dp-security-matrix",
    "シングルスレッドは、レジ係が 1 人の店。待つ仕事は預けられる。止まるのは、手を離さない仕事。手を離さない仕事が、後ろの客を全員待たせる",
    "手を離さない仕事が、後ろの客を全員待たせる。",
  ),
  "slack": glossaryVisualWithDark(
    "slack",
    "it-current-state",
    "Slack は、話題ごとにチャンネルを分ける。1 本の流れに混ぜず、返信はスレッドに束ねる。全員の発言が 1 本に流れるのではなく、話題ごとの棚に入る",
    "全員の発言が 1 本に流れるのではなく、話題ごとの棚に入る。",
  ),
  "slice": glossaryVisualWithDark(
    "slice",
    "sequence-oauth",
    "スライスは、パンを切る位置で決める。s[1:4] は 4 文字ではなく 3 文字。番号は文字の間の切れ目。始めの切れ目から終わりの切れ目まで",
    "番号は文字の間の切れ目。始めの切れ目から終わりの切れ目まで。",
  ),
  "snake-case": glossaryVisualWithDark(
    "snake-case",
    "architecture",
    "スネークケースは、小文字を _ でつなぐ書き方。Python の変数・関数名はこの形。JavaScript は別。snake_case は、単語を _ でつなぐ。使う形は言語ごとに違う",
    "snake_case は、単語を _ でつなぐ。使う形は言語ごとに違う。",
  ),
  "sns-boxplot": glossaryVisualWithDark(
    "sns-boxplot",
    "flowchart",
    "sns.boxplot は、データを箱とひげに畳む。箱の中の線は平均ではなく中央値。箱は真ん中の半分。線は中央値で、平均とは限らない",
    "箱は真ん中の半分。線は中央値で、平均とは限らない。",
  ),
  "sns-heatmap": glossaryVisualWithDark(
    "sns-heatmap",
    "sequence",
    "sns.heatmap は、数の大小を色の濃さで見せる。色は飾りではなく、右の帯が読み方の目安になる。この配色では濃いほど大きい数。右の帯で目安を読む",
    "この配色では濃いほど大きい数。右の帯で目安を読む。",
  ),
  "software-testing": glossaryVisualWithDark(
    "software-testing",
    "pyramid",
    "ソフトウェアテストは、答えを量って比べる検品。期待する答えと、動かした結果を、同じかどうかで比べる。通っても、バグが無い証明にはならない。量った入力の分だけ分かる",
    "通っても、バグが無い証明にはならない。量った入力の分だけ分かる。",
  ),
  "spa": glossaryVisualWithDark(
    "spa",
    "swimlane",
    "SPA は、最初に枠を受け取り付箋を貼り替える。画面を替えても、毎回丸ごとは届かない。最初に枠を受け取り、あとは必要なデータを取りに行って表示を更新する",
    "最初に枠を受け取り、あとは必要なデータを取りに行って表示を更新する。",
  ),
  "spread": glossaryVisualWithDark(
    "spread",
    "state",
    "スプレッドは、配列の中身を 1 つずつ広げる書き方。要素がオブジェクトなら、コピー後も同じ物を指す。スプレッドは、外の箱だけ新しくする。オブジェクトの要素は共有のまま",
    "スプレッドは、外の箱だけ新しくする。オブジェクトの要素は共有のまま。",
  ),
  "spread-syntax": glossaryVisualWithDark(
    "spread-syntax",
    "er",
    "スプレッドは、中身を新しい箱へ並べる。[...a, 4] は a に 4 を足すのではない。元の a は変わらない。ただし入れ物の中身は複製されない",
    "元の a は変わらない。ただし入れ物の中身は複製されない。",
  ),
  "sql": glossaryVisualWithDark(
    "sql",
    "flowchart",
    "SQL は、ほしい物だけを書く注文書。作り方は書かない。決めるのはデータベースの側。手順は書かず、ほしい結果を書く。MySQL も PostgreSQL もその注文書を読む",
    "手順は書かず、ほしい結果を書く。MySQL も PostgreSQL もその注文書を読む。",
  ),
  "ssl-certificate": glossaryVisualWithDark(
    "ssl-certificate",
    "timeline",
    "SSL/TLS 証明書は、公印つきの名前の賞状。鍵マークが言うのは、名前の一致と暗号化まで。鍵マークは、そのサイトが安全だという保証ではない",
    "鍵マークは、そのサイトが安全だという保証ではない。",
  ),
  "ssl-tls": glossaryVisualWithDark(
    "ssl-tls",
    "swimlane",
    "SSL/TLS は、道中で開けられない鍵付きの箱。SSL は旧い名前で、いま使うのは TLS。鍵は通信を守る印。サイトが安全かどうかは別",
    "鍵は通信を守る印。サイトが安全かどうかは別。",
  ),
  "stack": glossaryVisualWithDark(
    "stack",
    "state",
    "スタックは、皿の山のようなもの。最後に積んだ皿から、先に取る。最後に積んだ物が最初に出る。LIFO (後入れ先出し)",
    "最後に積んだ物が最初に出る。LIFO (後入れ先出し)",
  ),
  "stage": glossaryVisualWithDark(
    "stage",
    "quadrant",
    "ステージは、次のコミットに入れる変更を載せる台。git add は保存ではなく、台に載せる操作。ステージは、載せた変更だけがコミットされる。載せない変更は残る",
    "ステージは、載せた変更だけがコミットされる。載せない変更は残る。",
  ),
  "staging": glossaryVisualWithDark(
    "staging",
    "nested",
    "ステージングは、客席のない通し稽古場。本番に近い舞台装置で、お客さんの前に確かめる。ここで通ったものだけを、本番の舞台へ出す",
    "ここで通ったものだけを、本番の舞台へ出す。",
  ),
  "standard-library": glossaryVisualWithDark(
    "standard-library",
    "tree",
    "標準ライブラリは、言語に最初から付く道具箱。import で取り出し、足りない物だけ外から足す。言語に付属する物が標準、別に足す物が外部",
    "言語に付属する物が標準、別に足す物が外部。",
  ),
  "stateless": glossaryVisualWithDark(
    "stateless",
    "org-chart",
    "ステートレスは、前の注文を覚えない窓口。続きは、客が毎回見せる札で伝える。HTTP は前の回とのつながりを持たない。続きは、客が見せる札に頼る",
    "HTTP は前の回とのつながりを持たない。続きは、客が見せる札に頼る。",
  ),
  "static-hosting": glossaryVisualWithDark(
    "static-hosting",
    "layers",
    "静的ホスティングは、パンフレットラック。できた物をそのまま渡す。作って渡すのは別の仕組み。ファイルを置くだけで公開できる。サーバーで内容を作るプログラムはいらない",
    "ファイルを置くだけで公開できる。サーバーで内容を作るプログラムはいらない。",
  ),
  "static-typing": glossaryVisualWithDark(
    "static-typing",
    "venn",
    "静的型付けは、動かす前の検品。型は Python や JavaScript にもある。違うのは調べる時。静的は動かす前に、動的は動かして、その行に来たときに調べる",
    "静的は動かす前に、動的は動かして、その行に来たときに調べる。",
  ),
  "status-code": glossaryVisualWithDark(
    "status-code",
    "pyramid",
    "ステータスコードは、返事に押される 3 桁のハンコ。先頭の 1 桁で、何が起きたかの種類が分かる。先頭が 4 なら URL や権限、5 ならサーバー側を、まず確かめる",
    "先頭が 4 なら URL や権限、5 ならサーバー側を、まず確かめる。",
  ),
  "stock-deduction": glossaryVisualWithDark(
    "stock-deduction",
    "radar",
    "在庫減算は、確かめながら 1 手で引く更新。同時に買われても、売り越さない。在庫がある間だけ 1 つ引く。確かめと引き算を分けない",
    "在庫がある間だけ 1 つ引く。確かめと引き算を分けない。",
  ),
  "storage": glossaryVisualWithDark(
    "storage",
    "loop",
    "ストレージは、データの預け方が 3 種類ある。クラウドでは、使い方に合わせて置き場の型を選ぶ。丸ごと出し入れ、1 台に直結、みんなで共有。用途で選ぶ",
    "丸ごと出し入れ、1 台に直結、みんなで共有。用途で選ぶ。",
  ),
  "storage-device": glossaryVisualWithDark(
    "storage-device",
    "data-lake",
    "記憶装置は、作業机と書庫の 2 種類に分かれる。メモリは電源を切ると消え、ストレージは残る。保存するまでは机の上だけにある。切る前に書庫へ",
    "保存するまでは机の上だけにある。切る前に書庫へ。",
  ),
  "streaming": glossaryVisualWithDark(
    "streaming",
    "bar",
    "ストリーミングは、できた分から流すこと。動画だけの話ではない。待たずに届ける方式。全部そろうのを待たず、できた分から届ける",
    "全部そろうのを待たず、できた分から届ける。",
  ),
  "string-manipulation": glossaryVisualWithDark(
    "string-manipulation",
    "line",
    "文字列操作は、切る、つなぐ、変える。元は残る。新しい文字列が返り、元の文字列は書き換わらない。操作の答えは新しい文字列で、元の s はそのまま",
    "操作の答えは新しい文字列で、元の s はそのまま。",
  ),
  "string-valueof": glossaryVisualWithDark(
    "string-valueof",
    "data-flow",
    "String.valueOf は、null も 4 文字の札にする。Object 型の変数に入った null でも、例外にならない。null と直に書くと別の版が選ばれて落ちる。Object 型の変数に入れて渡す",
    "null と直に書くと別の版が選ばれて落ちる。Object 型の変数に入れて渡す。",
  ),
  "subnet": glossaryVisualWithDark(
    "subnet",
    "gantt",
    "サブネットは、敷地を切った区画。公開か非公開かは、インターネットゲートウェイ (igw) への道で決まる。公開と非公開を分けるのは、区画の名前ではなく igw への直通の道",
    "公開と非公開を分けるのは、区画の名前ではなく igw への直通の道。",
  ),
  "subquery": glossaryVisualWithDark(
    "subquery",
    "scatter",
    "サブクエリは、先に答えを出す内側の依頼。この例では、括弧の中が先に答えを出し、外側の条件になる。内側の答えを付箋に書いて、外側の条件にはめ込む",
    "内側の答えを付箋に書いて、外側の条件にはめ込む。",
  ),
  "supervised-learning": glossaryVisualWithDark(
    "supervised-learning",
    "swimlane",
    "教師あり学習は、答え付きの問題集で練習する。練習用と、見せていない確認用を分けて使う。訓練データだけで測ると、丸暗記でも高得点になる",
    "訓練データだけで測ると、丸暗記でも高得点になる。",
  ),
  "swimlane": glossaryVisualWithDark(
    "swimlane",
    "medallion",
    "スイムレーンは、誰の仕事かをプールのコースで分ける図。コースは時間ではなく担当。この図は、順番を上から下へ並べた。コースをまたぐ矢印が、仕事の手渡し。抜けや待ちを探す目印になる",
    "コースをまたぐ矢印が、仕事の手渡し。抜けや待ちを探す目印になる。",
  ),
  "sync": glossaryVisualWithDark(
    "sync",
    "high-level",
    "同期は、終わるのを待ってから次の行へ進む動き。「同時」ではなく、書いた順に 1 つずつ。待つ間、その流れは止まる。そのかわり、書いた順に動く",
    "待つ間、その流れは止まる。そのかわり、書いた順に動く。",
  ),
  "sync-message": glossaryVisualWithDark(
    "sync-message",
    "process",
    "同期メッセージは、返事が来るまで呼んだ側が待つ呼び出し。シーケンス図では、塗りつぶした矢じりの実線で描く。実線は呼び出し、点線は戻り。戻りが来るまで呼んだ側は待つ",
    "実線は呼び出し、点線は戻り。戻りが来るまで呼んだ側は待つ。",
  ),
  "synthetic-event": glossaryVisualWithDark(
    "synthetic-event",
    "data-flow",
    "合成イベントは、ブラウザの違いをならした React のイベント。onClick に届く e は、React が包み直した物。本物のイベントが要るときは、e.nativeEvent から取り出す",
    "本物のイベントが要るときは、e.nativeEvent から取り出す。",
  ),
  "system-call": glossaryVisualWithDark(
    "system-call",
    "dp-integration",
    "システムコールは、カーネルへの申込窓口。関数を呼ぶのとは違い、壁を越えて頼む唯一の入口。アプリはシステムコールを通してだけ、カーネルに頼める",
    "アプリはシステムコールを通してだけ、カーネルに頼める。",
  ),
  "system-diagram": glossaryVisualWithDark(
    "system-diagram",
    "high-level-parametric",
    "システム構成図は、機器とサービスをつなぐ街の地図。コードの中ではなく、動いている部品と通信を上から見る。建物が動く機器やサービス、道が通信。関数やクラスは描かない",
    "建物が動く機器やサービス、道が通信。関数やクラスは描かない。",
  ),
  "team-development": glossaryVisualWithDark(
    "team-development",
    "dp-security-matrix",
    "チーム開発は、各自の机で作り、共有の棚へ合流する。同じファイルを同時に直接直すのではなく、写しで作って見てもらう。多くのチームは、プルリクエストで見てもらってから棚に入れる",
    "多くのチームは、プルリクエストで見てもらってから棚に入れる。",
  ),
  "template": glossaryVisualWithDark(
    "template",
    "it-current-state",
    "テンプレートは、空欄を残した賞状の型。値を入れると写しが別にできて、型は空欄のまま残る。型は何度でも使える。入れるたびに別の 1 枚ができる",
    "型は何度でも使える。入れるたびに別の 1 枚ができる。",
  ),
  "template-literal": glossaryVisualWithDark(
    "template-literal",
    "sequence-oauth",
    "テンプレートリテラルは、穴あきの便せん。値が入るのは、バッククォートで囲んだときだけ。穴の中は式でもよい。囲み方で結果が変わる",
    "穴の中は式でもよい。囲み方で結果が変わる。",
  ),
  "terminal": glossaryVisualWithDark(
    "terminal",
    "architecture",
    "ターミナルは、シェルへの窓口。黒い画面が実行するのではなく、実行を頼む入口。ls のような外部コマンドは、シェルが OS に頼んで実行する",
    "ls のような外部コマンドは、シェルが OS に頼んで実行する。",
  ),
  "termination-condition": glossaryVisualWithDark(
    "termination-condition",
    "flowchart",
    "終了条件は、ループが止まる境目の条件。条件が偽になった周で、ループは終わる。この例では、n を変えないと条件は真のままで終わらない",
    "この例では、n を変えないと条件は真のままで終わらない。",
  ),
  "terminator": glossaryVisualWithDark(
    "terminator",
    "sequence",
    "端子は、流れ図の始まりと終わりを示す角丸の記号。処理の四角や判断の菱形とは、別の形で描く。始まりは 1 つ、終わりは複数あってよい。どちらも処理ではない",
    "始まりは 1 つ、終わりは複数あってよい。どちらも処理ではない。",
  ),
  "ternary-operator": glossaryVisualWithDark(
    "ternary-operator",
    "state",
    "三項演算子は、条件で 2 つの値から 1 つを選ぶ式。if 文と違い、選んだ値がそのまま返る。3 つの部品で 1 つの値になる。選んだ値は、そのまま変数に入る",
    "3 つの部品で 1 つの値になる。選んだ値は、そのまま変数に入る。",
  ),
  "test": glossaryVisualWithDark(
    "test",
    "pyramid",
    "テストは、決めた入力で答えを秤にかける。通っても、秤に乗せなかった入力は見ていない。通ってもバグが無いとは言えない。試した分だけが確かめられる",
    "通ってもバグが無いとは言えない。試した分だけが確かめられる。",
  ),
  "thread": glossaryVisualWithDark(
    "thread",
    "er",
    "スレッドは、同じ厨房で動くコック。1 つのプログラムの中で、仕事を分ける。同期せずに同じ棚を同時に書き換えると、数がずれることがある",
    "同期せずに同じ棚を同時に書き換えると、数がずれることがある。",
  ),
  "time-complexity": glossaryVisualWithDark(
    "time-complexity",
    "timeline",
    "時間計算量は、歩数計で数える手数。秒数ではなく、データが増えたときの手数。機械が変わっても、データの数に対する手数の増え方は変わらない",
    "機械が変わっても、データの数に対する手数の増え方は変わらない。",
  ),
  "time-perf-counter": glossaryVisualWithDark(
    "time-perf-counter",
    "swimlane",
    "time.perf_counter は、差を取って使う。返る値そのものは、時刻ではない。2 回読んで引き算すると、かかった秒になる",
    "2 回読んで引き算すると、かかった秒になる。",
  ),
  "timestamp": glossaryVisualWithDark(
    "timestamp",
    "quadrant",
    "タイムスタンプは、基準の時刻からの経過時間の数。地域の時刻ではなく、世界で同じ 1 つの数。タイムスタンプは、同じ数を地域ごとの時刻に直して見る",
    "タイムスタンプは、同じ数を地域ごとの時刻に直して見る。",
  ),
  "traceback": glossaryVisualWithDark(
    "traceback",
    "nested",
    "トレースバックは、下から読む。来た道を古い順に並べ、最後の行が穴の正体。いちばん下が起きたこと。上へたどると、来た道が分かる",
    "いちばん下が起きたこと。上へたどると、来た道が分かる。",
  ),
  "tracing": glossaryVisualWithDark(
    "tracing",
    "gantt",
    "トレーシングは、依頼 1 件の追跡票。ログの別名ではなく、サービスをまたぐ 1 本の線。追跡番号が同じ区間を並べると、遅い所が一目で分かる",
    "追跡番号が同じ区間を並べると、遅い所が一目で分かる。",
  ),
  "tradeoff": glossaryVisualWithDark(
    "tradeoff",
    "quadrant",
    "トレードオフは、何かを得る代わりに何かを払う。この例では、探すのを速くする索引が場所を使う。付箋と索引は探すのを速めるが、その分の棚の場所を使う",
    "付箋と索引は探すのを速めるが、その分の棚の場所を使う。",
  ),
  "transformer": glossaryVisualWithDark(
    "transformer",
    "bar",
    "Transformer は、文中の語どうしを見比べる。前から順に読むのではなく、糸の太さで注目を決める。語どうしを見比べて注目を決め、次は 1 つずつ足す",
    "語どうしを見比べて注目を決め、次は 1 つずつ足す。",
  ),
  "transparency": glossaryVisualWithDark(
    "transparency",
    "tree",
    "透明性は、後ろが透けて見える度合い。CSS の opacity は 1 で不透明、0 で完全に透明。opacity は中身ごと薄くなる。背景だけなら、アルファ付きの色で書く",
    "opacity は中身ごと薄くなる。背景だけなら、アルファ付きの色で書く。",
  ),
  "tree": glossaryVisualWithDark(
    "tree",
    "tree",
    "tree は、本の目次のような形。根から枝分かれして、葉に着く。根から葉までの道は 1 本だけ。深さは、根から何歩かで数える",
    "根から葉までの道は 1 本だけ。深さは、根から何歩かで数える。",
  ),
  "tree-structure": glossaryVisualWithDark(
    "tree-structure",
    "org-chart",
    "木構造は、逆さにした木の形。根から枝分かれして、葉に着く。どの子にも親は 1 人だけ、子は何人でも。輪はできない",
    "どの子にも親は 1 人だけ、子は何人でも。輪はできない。",
  ),
  "try-catch": glossaryVisualWithDark(
    "try-catch",
    "org-chart",
    "try-catch は、例外が出たら catch へ移る。try の残りは飛ばされ、catch のあとから続く。例外が起きた行で try は終わる。catch は書いた型か、その子の型だけ受ける",
    "例外が起きた行で try は終わる。catch は書いた型か、その子の型だけ受ける。",
  ),
  "try-except": glossaryVisualWithDark(
    "try-except",
    "layers",
    "try-except は、型を名指しして受ける。起きなければ except は動かない。except: とだけ書いて何もしないと、バグも隠れる",
    "except: とだけ書いて何もしないと、バグも隠れる。",
  ),
  "try-with-resources": glossaryVisualWithDark(
    "try-with-resources",
    "venn",
    "try-with-resources は、使い終えたら自動で閉じる。close を書かなくても、ブロックを出るときに返る。例外が出ても close が呼ばれる。後に開いた方から先に閉じる",
    "例外が出ても close が呼ばれる。後に開いた方から先に閉じる。",
  ),
  "tsconfig-json": glossaryVisualWithDark(
    "tsconfig-json",
    "pyramid",
    "tsconfig.json は、変換機 tsc に渡す設定票。tsc がプロジェクトを JavaScript に直すときに読む。tsc で直した JavaScript を node で動かす。node は tsconfig.json を読まない",
    "tsc で直した JavaScript を node で動かす。node は tsconfig.json を読まない。",
  ),
  "tuple": glossaryVisualWithDark(
    "tuple",
    "radar",
    "タプルは、封をしたケース。リストと似ているが、作ったあとは入れ替えられない。変えたいときは、中身を入れ替えず新しいタプルを作る",
    "変えたいときは、中身を入れ替えず新しいタプルを作る。",
  ),
  "type-annotation": glossaryVisualWithDark(
    "type-annotation",
    "loop",
    "型注釈は、変数や引数に書き添える型の札。書いた型は残るが、値は縛らない。注釈は書いて残すだけ。値の型を決める仕組みではない",
    "注釈は書いて残すだけ。値の型を決める仕組みではない。",
  ),
  "type-conversion": glossaryVisualWithDark(
    "type-conversion",
    "data-lake",
    "型変換は、値を別の型の新しい値にすること。元の値は書き換わらない。int(s) は新しい数を返すだけ。s の文字はそのまま残る",
    "int(s) は新しい数を返すだけ。s の文字はそのまま残る。",
  ),
  "type-hint": glossaryVisualWithDark(
    "type-hint",
    "bar",
    "型ヒントは、実行時には強制されない荷札。型を調べるのは、別の検査の道具 (mypy など)。荷札は検査の道具が読む。Python は読まずに動かす",
    "荷札は検査の道具が読む。Python は読まずに動かす。",
  ),
  "type-inference": glossaryVisualWithDark(
    "type-inference",
    "line",
    "型推論は、書かなくても値から型を決めてくれる。型が無いのではなく、自動で付いている。付いた型と違う物を入れるとエラーになる。決まらない引数は自分で書く",
    "付いた型と違う物を入れるとエラーになる。決まらない引数は自分で書く。",
  ),
  "type-safety": glossaryVisualWithDark(
    "type-safety",
    "gantt",
    "型安全は、合わない型の使い方を動かす前に見つける。数を入れる所に、文字は入らない。見つけるのは型の食い違いだけ。型が合っていれば、値の間違いは通る",
    "見つけるのは型の食い違いだけ。型が合っていれば、値の間違いは通る。",
  ),
  "typescript": glossaryVisualWithDark(
    "typescript",
    "flowchart",
    "TypeScript の型は、動かす前の検査。検査のあと、型は消えて JavaScript になる。出力された JavaScript に型注釈は残らない。確かめるのは動かす前だけ",
    "出力された JavaScript に型注釈は残らない。確かめるのは動かす前だけ。",
  ),
  "unsupervised-learning": glossaryVisualWithDark(
    "unsupervised-learning",
    "scatter",
    "教師なし学習は、答え無しでデータの形を探す。クラスタリングの例。山の名前は、あとから人が付ける。番号の 0、1、2 に意味はない。中を見て人が名前を付ける",
    "番号の 0、1、2 に意味はない。中を見て人が名前を付ける。",
  ),
  "url": glossaryVisualWithDark(
    "url",
    "layers",
    "URL は、ページごとの住所。サイトの名前ではなく、部屋番号まで書く。建物 (ホスト) が同じでも、パスでページを指し分ける",
    "建物 (ホスト) が同じでも、パスでページを指し分ける。",
  ),
  "usefetch": glossaryVisualWithDark(
    "usefetch",
    "scatter",
    "useFetch は、React に入っていない自作の取得フック。よくある実装では、取得の途中と結果を loading、data、error で返す。名前は use で始める決まり。この例の中身は useState、useEffect、fetch の組み合わせ",
    "名前は use で始める決まり。この例の中身は useState、useEffect、fetch の組み合わせ。",
  ),
  "uselocalstorage": glossaryVisualWithDark(
    "uselocalstorage",
    "medallion",
    "useLocalStorage は、画面と保存をつなぐ自作のフック。React に最初からある物ではなく、2 つを 1 つにまとめた書き方。画面の更新と、再読み込みしても残る保存を、1 つのフックで受け持つ",
    "画面の更新と、再読み込みしても残る保存を、1 つのフックで受け持つ。",
  ),
  "validation": glossaryVisualWithDark(
    "validation",
    "high-level",
    "バリデーションは、受け取り口でも検札する。ブラウザの入力チェックは親切のため。守りはサーバー。ブラウザを通らず直接送られても、サーバーが断る",
    "ブラウザを通らず直接送られても、サーバーが断る。",
  ),
  "variable": glossaryVisualWithDark(
    "variable",
    "process",
    "変数は、名札をつけた引き出し。「=」は「等しい」ではなく「入れる」。右を先に計算して、引き出しに入れ直す。前の値は消える",
    "右を先に計算して、引き出しに入れ直す。前の値は消える。",
  ),
  "vectorization": glossaryVisualWithDark(
    "vectorization",
    "data-flow",
    "ベクトル化は、文を意味の座標に置くこと。文字に番号を振るのではなく、意味の近さを数にする。同じモデルで数にするから、意味が近い文を探せる",
    "同じモデルで数にするから、意味が近い文を探せる。",
  ),
  "view": glossaryVisualWithDark(
    "view",
    "dp-integration",
    "ビューは、元の表をのぞく窓。データはコピーせず、検索の文を名前をつけて持つ。窓の景色は元の表そのもの。元を直して見直すと、景色も変わる",
    "窓の景色は元の表そのもの。元を直して見直すと、景色も変わる。",
  ),
  "virtual-environment": glossaryVisualWithDark(
    "virtual-environment",
    "nested",
    "仮想環境は、プロジェクトごとの道具箱。ライブラリは、箱の中にだけ入れる。箱を分ければ、A は旧い版、B は新しい版が同時に成り立つ",
    "箱を分ければ、A は旧い版、B は新しい版が同時に成り立つ。",
  ),
  "waterfall": glossaryVisualWithDark(
    "waterfall",
    "high-level-parametric",
    "ウォーターフォールは、段を終えてから次へ流す開発。古いやり方、ではなく、仕様が固い仕事に向く進め方。上へ戻すと費用が大きい。だから各段で、確かめてから次へ進む",
    "上へ戻すと費用が大きい。だから各段で、確かめてから次へ進む。",
  ),
  "web-inf": glossaryVisualWithDark(
    "web-inf",
    "dp-security-matrix",
    "WEB-INF は、ブラウザからは入れない奥の部屋。URL で直接開こうとしても返ってこず、サーバー側のプログラムからは読める。見せたくない設定やプログラムは、WEB-INF の中に置く",
    "見せたくない設定やプログラムは、WEB-INF の中に置く。",
  ),
  "web-xml": glossaryVisualWithDark(
    "web-xml",
    "it-current-state",
    "web.xml は、URL とサーブレットの対応も書く設定ファイル。画面ではなく、どの要求をどのクラスに渡すかを書く。糸で結ぶのは web.xml でも注釈でもよく、動きは同じ",
    "糸で結ぶのは web.xml でも注釈でもよく、動きは同じ。",
  ),
  "wireframe": glossaryVisualWithDark(
    "wireframe",
    "sequence-oauth",
    "ワイヤーフレームは、画面の骨組みだけを先に描く。色も写真も入れず、何をどこに置くかを決める。見た目ではなく、構成と配置の下書き。配置を簡単に描き直せる",
    "見た目ではなく、構成と配置の下書き。配置を簡単に描き直せる。",
  ),
  "workflow": glossaryVisualWithDark(
    "workflow",
    "architecture",
    "ワークフローは、自動で動かす作業の指示書。GitHub Actions では、job は同時に走り、step は上から順に進む。並べるだけなら同時。順番が要る所に needs を書く",
    "並べるだけなら同時。順番が要る所に needs を書く。",
  ),
  "working-tree": glossaryVisualWithDark(
    "working-tree",
    "flowchart",
    "ワーキングツリーは、履歴ではなく作業机の上。commit するまで、机の上の変更は書庫 (履歴) に入らない。commit して初めて、書庫に残る",
    "commit して初めて、書庫に残る。",
  ),
  "xss": glossaryVisualWithDark(
    "xss",
    "flowchart",
    "XSS は、掲示板の付箋が命令として動くこと。書き込みは文字のはずが、ブラウザは命令として読む。出す場所に合う対策で、書き込みを命令にしない",
    "出す場所に合う対策で、書き込みを命令にしない。",
  ),
  "zero-downtime": glossaryVisualWithDark(
    "zero-downtime",
    "it-current-state",
    "ゼロダウンタイムは、線路を切り替える更新。無停止更新の一例。新しい版を横に立てて、向きを変える。新しい版を確かめてから向きを変える。利用者は待たない",
    "新しい版を確かめてから向きを変える。利用者は待たない。",
  ),
  "zero-shot": glossaryVisualWithDark(
    "zero-shot",
    "high-level",
    "Zero-shot は、手本を 0 枚にして頼むこと。ゼロなのは手本の数。学習済みの知識は使う。手本なしで解けることも多い。外れたら手本を足す",
    "手本なしで解けることも多い。外れたら手本を足す。",
  ),
};

export function getGlossaryVisual(slug: string): GlossaryVisual | undefined {
  return GLOSSARY_VISUALS[slug];
}
